<template>
  <div class="welcome-container">
    <div class="table-header">
      <div class="title-container">
        <div class="title-left">
          <h1 class="title">{{$t('Tickets')}}</h1>
          <button class="btn btn-add-ticket btn-ticket-margin" @click="openTicketForm" :title="$t('agregar_ticket')">
            {{ $t('nuevo_ticket') }}
          </button>
        </div>
        <div class="title-actions">
          <!-- Mostrar búsqueda y botón de filtros aquí (móvil y escritorio) -->
          <div class="search-input-wrapper" style="min-width:140px; max-width:560px;">
            <input
              id="busquedaUnificada"
              v-model="searchQuery"
              type="search"
              class="filter-input"
              :placeholder="$t('numero_o_descripcion') || 'Nº ticket o texto en la descripción...'"
              @input="filterTickets"
              @keydown.enter.prevent="filterTickets"
            />
            <button
              v-if="searchQuery"
              type="button"
              class="material-icons clear-icon"
              @click="clearSearch"
              aria-label="Limpiar"
              title="Limpiar"
            >
              clear
            </button>
            <button
              type="button"
              class="material-icons search-icon"
              @click="filterTickets"
              aria-label="Buscar"
              title="Buscar"
            >
              search
            </button>
          </div>

          <div class="filter-actions">
            <button v-if="isMobile" class="btn filters-toggle-btn" @click="toggleFilters" :aria-expanded="!filtersCollapsed" :title="filtersCollapsed ? ($t('mostrar_filtros') || 'Mostrar filtros') : ($t('ocultar_filtros') || 'Ocultar filtros')">
              <span class="btn-text">{{ $t('filtros') || 'Filtros' }}</span>
              <span class="arrow" :class="{ open: !filtersCollapsed }">▼</span>
            </button>
          </div>
          <!-- Debug: mostrar datos cargados del usuario logueado -->
          <div class="user-data-debug" style="margin-left:12px; display:flex; align-items:center; gap:8px;">
            <!-- Button removed as requested -->
            <div v-if="showUserDataVisible" style="background:#f7f7f7; border:1px solid #e1e1e1; padding:8px; border-radius:6px; max-width:720px; max-height:320px; overflow:auto;">
              <strong>{{ $t('datos_usuario_logueado') || 'Datos usuario logueado' }}:</strong>
              <ul style="margin:6px 0 0 16px; padding:0; list-style:disc;">
                <li v-for="item in userDataArray" :key="item.key">
                  <code style="font-weight:600">{{ item.key }}</code>: <span>{{ String(item.value) }}</span>
                </li>
                <li v-if="userDataArray.length === 0">(vacío)</li>
              </ul>
              <details style="margin-top:8px;"><summary>JSON raw</summary><pre style="white-space:pre-wrap;word-break:break-word">{{ JSON.stringify(currentUserData, null, 2) }}</pre></details>
            </div>
          </div>
        </div>
      </div>

      <!-- Fila de filtros: mostrarse debajo de la búsqueda en vista web; colapsable en móvil -->
      <div class="filters-row filters-row-align" v-show="!filtersCollapsed">
        <!-- Búsqueda y acciones agrupadas en la primera columna de filtros: ahora aparece debajo del título 'Tickets' y el botón 'Nuevo Ticket' -->
        <!-- filter-row-top trasladado a la cabecera para visibilidad en móvil -->
        <!-- etiqueta 'Filtrar' eliminada por petición -->
        <!-- 1) Filtrar por estado: en móvil se muestra aquí, en desktop se muestra en la cabecera -->
        <div class="filter-group" v-show="isMobile">
          <label for="estado" class="filter-label">{{ $t('estado') }}</label>
          <select
            id="estado"
            v-model="selectedEstado"
            class="filter-select"
            @change="filterTickets"
          >
            <option value="">{{ $t('todos') }}</option>
            <option value="Abiertos y En Proceso">{{ $t('abiertos_y_en_proceso') }}</option>
            <option value="Abierto">{{ $t('abierto') }}</option>
            <option value="En Proceso">{{ $t('en_proceso') }}</option>
            <option value="Cerrado">{{ $t('cerrado') }}</option>
          </select>
        </div>

        <!-- 2) Filtrar por categoría (solo para roles 1/2/3) -->
        <!-- Mostrar filtro de 'estado' también en escritorio a la izquierda del filtro de categoría -->
        <div class="filter-group" v-show="!isMobile">
          <label for="estado_desktop" class="filter-label">{{ $t('estado') }}</label>
          <select
            id="estado_desktop"
            v-model="selectedEstado"
            class="filter-select"
            @change="filterTickets"
          >
            <option value="">{{ $t('todos') }}</option>
            <option value="Abiertos y En Proceso">{{ $t('abiertos_y_en_proceso') }}</option>
            <option value="Abierto">{{ $t('abierto') }}</option>
            <option value="En Proceso">{{ $t('en_proceso') }}</option>
            <option value="Cerrado">{{ $t('cerrado') }}</option>
          </select>
        </div>

        <template v-if="userRole === 1 || userRole === 2 || userRole === 3">
          <div class="filter-group">
            <label for="categoria" class="filter-label">{{ $t('filtrar_categoria') }}</label>
            <select
              id="categoria"
              v-model="selectedCategoria"
              class="filter-select"
              @change="filterTickets"
            >
              <option value="">{{ $t('todas') }}</option>
              <option v-for="categoria in categorias" :key="categoria.id" :value="categoria.nombre">
                {{ categoria.nombre }}
              </option>
            </select>
          </div>

          <!-- 3) Mostrar (solo para roles 1/2/3) -->
          <div class="filter-group">
            <label for="borrado" class="filter-label">{{ $t('mostrar') }}</label>
            <select
              id="borrado"
              v-model="selectedBorrado"
              class="filter-select"
              @change="filterTickets"
            >
              <option value="no-borrados">{{ $t('no_borrados') }}</option>
              <option value="borrados">{{ $t('borrados') }}</option>
              <option value="todos">{{ $t('todos') }}</option>
            </select>
          </div>
        </template>

  <!-- 4) Filtrar por asignado (roles 1/2/3: administradores, supervisores y técnicos) -->
  <template v-if="userRole === 1 || userRole === 2 || userRole === 3">
          <div class="filter-group">
            <label for="asignado" class="filter-label">{{ $t('filtrar_asignado') }}</label>
            <select
              id="asignado"
              v-model="selectedAsignado"
              class="filter-select"
              @change="filterTickets"
            >
              <option value="">{{ $t('todos') }}</option>
              <option value="Sin asignar">{{ $t('sin_asignar') }}</option>
              <option
                v-for="usuario in nombresAsignados"
                :key="usuario"
                :value="usuario"
              >
                {{ usuario }}
              </option>
            </select>
          </div>
        </template>
      </div>
      
      <!-- end filters-row -->
    </div>
      <!-- Contenedor de la tabla -->
      <!-- Fallback: header fijo solo para móvil que mostrará los tres títulos extra si sticky no funciona -->

      <div class="table-container">
        <!-- Agrupador de cabeceras: contiene la tabla de encabezado y la segunda fila móvil -->
        <div class="headers-stack">
        <!-- Header table: contains only the thead. Kept as a separate table so its column sizing
             does not force the tbody column widths. -->
        <div class="table-header-wrapper">
          <table class="user-table user-table-header" aria-hidden="false">
            <colgroup>
              <col class="c-numero">
              <col class="c-fecha">
              <col class="c-incidencia">
              <col class="c-creado">
              <col class="c-ubicacion">
              <col class="c-categoria">
              <col class="c-asignado">
              <col class="c-nivel">
              <col class="c-estado">
              <col class="c-acciones">
            </colgroup>
            <thead>
              <template v-if="!isMobile">
                <tr class="desktop-header-row">
                  <th class="col-numero">{{ $t('numero') }}</th>
                  <th class="col-fecha">{{ $t('fecha_hora') }}</th>
                  <th class="col-incidencia">{{ $t('incidencia') }}</th>
                  <th class="col-creado">{{ $t('creado') }}</th>
                  <th class="col-ubicacion">{{ $t('ubicacion') }}</th>
                  <th class="col-categoria">{{ $t('categoria') }}</th>
                  <th class="col-asignado">{{ $t('asignado') }}</th>
                  <th class="col-nivel">{{ $t('nivel') }}</th>
                  <th class="col-estado">{{ $t('estado') }}</th>
                  <th class="col-acciones">{{ $t('acciones') }}</th>
                </tr>
              </template>
              <template v-else>
                <tr class="desktop-header-row first-row">
                  <th class="col-numero">{{ $t('numero') }}</th>
                  <th class="col-fecha">{{ $t('fecha_hora') }}</th>
                  <th class="col-incidencia">{{ $t('incidencia') }}</th>
                  <th class="col-creado hide-on-first">{{ $t('creado') }}</th>
                  <th class="col-ubicacion hide-on-first">{{ $t('ubicacion') }}</th>
                  <th class="col-categoria hide-on-first">{{ $t('categoria') }}</th>
                  <th class="col-asignado hide-on-first">{{ $t('asignado') }}</th>
                  <th class="col-nivel hide-on-first">{{ $t('nivel') }}</th>
                  <th class="col-estado hide-on-first">{{ $t('estado') }}</th>
                  <th class="col-acciones hide-on-first">{{ $t('acciones') }}</th>
                </tr>
              </template>
            </thead>
          </table>
     </div>
       <div class="mobile-fixed-second-header" aria-hidden="true">
          <div class="mfh-item">{{ $t('asignado') }}</div>
          <div class="mfh-item">{{ $t('ubicacion') }}</div>
          <div class="mfh-item">{{ $t('categoria') }}</div>
        </div>   

     </div> <!-- .headers-stack -->

        <!-- Body table: scrollable wrapper so tbody width/cols can be independent -->
        <div class="table-body-scroll">
          <table class="user-table user-table-body">
            <colgroup>
              <col class="c-numero">
              <col class="c-fecha">
              <col class="c-incidencia">
              <col class="c-creado">
              <col class="c-ubicacion">
              <col class="c-categoria">
              <col class="c-asignado">
              <col class="c-nivel">
              <col class="c-estado">
              <col class="c-acciones">
            </colgroup>
            <tbody>
            <template v-for="ticket in filteredTickets" :key="ticket.id">
              <tr
                :class="getPriorityClass(ticket.prioridad_valor)"
                @dblclick="((userRole === 1 || userRole === 2) || (userRole === 3 && (ticket.asignado_a === userName || ticket.creado_por === userName)) || (userRole === 4 && (ticket.creado_por === userName || ticket.asignado_a === userName))) && !ticket.borrado && openEditModal(ticket)"
              >
                <td class="col-numero">
                  <span
                    v-if="ticket.tiene_adjuntos"
                    class="material-icons"
                    style="vertical-align: middle; color: #798a99; font-size: 18px;"
                    :title="$t('tiene_adjuntos')"
                  >
                    attach_file
                  </span>
                  {{ ticket.id }}
                  <!-- Badge eliminado: no mostrar 'Cerrado' en la columna número -->
                </td>
                <td class="col-fecha">{{ formatFechaHora(ticket.fecha_hora) }}</td>
                <td class="col-incidencia">
                  <span v-if="!ticket.showFullDescription">
                    <!-- If this is a fichaje ticket (categoria_id === 4) show the first two lines only -->
                    <template v-if="ticket.categoria_id === 4">
                      <span v-html="previewByLines(ticket.descripcion || '', 2)"></span>
                      <a v-if="hasMoreLines(ticket.descripcion || '', 2)" href="#" @click.prevent="toggleDescription(ticket)"> {{ $t('ver_mas') }} </a>
                    </template>
                    <template v-else>
                      {{ truncateText(ticket.descripcion || '', 95) }}
                      <a v-if="(ticket.descripcion || '').length > 90" href="#" @click.prevent="toggleDescription(ticket)">{{ $t('ver_mas') }}</a>
                    </template>
                  </span>
                  <span v-else>
                    {{ ticket.descripcion }}
                    <a href="#" @click.prevent="toggleDescription(ticket)">{{ $t('ver_menos') }}</a>
                  </span>
                </td>
                <td class="col-creado">
                  <span v-if="!ticket.showFullCreado">
                    {{ truncateWords(ticket.creado_por, 2) }}
                    <a v-if="wordCount(ticket.creado_por) > 2" href="#" @click.prevent="toggleCreado(ticket)">{{ $t('ver_mas') }}</a>
                  </span>
                  <span v-else>
                    {{ ticket.creado_por }}
                    <a href="#" @click.prevent="toggleCreado(ticket)">{{ $t('ver_menos') }}</a>
                  </span>
                </td>
                <td class="col-ubicacion">{{ ticket.departamento }} - {{ ticket.delegacion }}</td>
                <td class="col-categoria">
                  {{ ticket.categoria || 'Sin categoría' }}

                </td>
                <td class="col-asignado">
                  <span v-if="!ticket.showFullAsignado">
                    {{ truncateWords(ticket.asignado_a, 2) }}
                    <a v-if="wordCount(ticket.asignado_a) > 2" href="#" @click.prevent="toggleAsignado(ticket)">{{ $t('ver_mas') }}</a>
                  </span>
                  <span v-else>
                    {{ ticket.asignado_a }}
                    <a href="#" @click.prevent="toggleAsignado(ticket)">{{ $t('ver_menos') }}</a>
                  </span>
                </td>
                <td class="col-nivel"><span class="nivel-pill">{{ $t('nivel_' + (ticket.prioridad || '').toLowerCase()) }}</span></td>
                <td class="col-estado">{{ $t('estado_' + (ticket.estado || '').toLowerCase().replace(/ /g, '_')) }}</td>
                <td class="col-acciones">
                  <!-- Botón móvil '+' que abre el menú de acciones -->
                  <button
                    class="mobile-actions-btn btn btn-sm btn-outline-secondary"
                    @click.stop="toggleActions(ticket)"
                    :aria-expanded="openActionsFor === ticket.id"
                    :title="$t('acciones')"
                  >
                    +
                  </button>

                  <!-- Menú de acciones móvil (visible cuando openActionsFor === ticket.id) -->
                  <div v-if="openActionsFor === ticket.id" class="mobile-actions-menu" @click.stop>
                    <!-- Reutilizamos los mismos botones/acciones que en desktop -->
                    <div class="mobile-action-item" v-if="userRole !== 4">
                      <template v-if="!ticket.borrado">
                        <template v-if="userRole === 3">
                          <button v-if="(ticket.asignado_a === userName || ticket.creado_por === userName)" class="btn btn-sm" @click="handleMobileEdit(ticket)">{{ ticket.estado === 'Cerrado' ? ($t('ver') || 'Ver') : $t('editar') }}</button>
                          <button v-if="(ticket.asignado_a === userName || ticket.creado_por === userName)" class="btn btn-sm" @click="handleMobileLock(ticket)">{{ ticket.estado !== 'Cerrado' ? $t('cerrar_ticket') : $t('reabrir_ticket') }}</button>
                        </template>
                        <template v-else>
                          <button v-if="!ticket.borrado" class="btn btn-sm" @click="handleMobileEdit(ticket)">{{ ticket.estado === 'Cerrado' ? ($t('ver') || 'Ver') : $t('editar') }}</button>
                          <button v-if="ticket.estado !== 'Cerrado'" class="btn btn-sm" @click="handleMobileAssign(ticket)">{{ $t('asignar_usuario') }}</button>
                          <button class="btn btn-sm" @click="handleMobileLock(ticket)">{{ ticket.estado !== 'Cerrado' ? $t('cerrar_ticket') : $t('reabrir_ticket') }}</button>
                          <button class="btn btn-sm" @click="handleMobileLogs(ticket)">{{ $t('ver_logs') }}</button>
                          <button v-if="ticket.estado !== 'Cerrado'" class="btn btn-sm btn-danger" @click="handleMobileDelete(ticket)">{{ $t('eliminar_ticket') }}</button>
                        </template>
                      </template>
                      <template v-else>
                        <button class="btn btn-sm" @click="handleMobileLogs(ticket)">{{ $t('ver_logs') }}</button>
                        <button v-if="ticket.estado !== 'Cerrado'" class="btn btn-sm btn-warning" @click="handleMobileRestore(ticket)">{{ $t('restaurar_ticket') }}</button>
                      </template>
                    </div>

                    <div class="mobile-action-item" v-else>
                      <template v-if="!ticket.borrado">
                        <button v-if="(ticket.creado_por_id === userId || ticket.asignado_a_id === userId)" class="btn btn-sm" @click="handleMobileEdit(ticket)">{{ ticket.estado === 'Cerrado' ? ($t('ver') || 'Ver') : $t('editar') }}</button>
                        <button v-if="ticket.asignado_a_id === userId" class="btn btn-sm" @click="handleMobileLock(ticket)">{{ ticket.estado !== 'Cerrado' ? $t('cerrar_ticket') : $t('reabrir_ticket') }}</button>
                      </template>
                    </div>
                  </div>

                  <!-- Desktop action container (se mantiene pero se ocultará en móvil vía CSS) -->
                  <div class="action-container desktop-actions" @dblclick.stop>
                    <!-- Desktop actions unificadas y bien formadas -->
                    <template v-if="!ticket.borrado">
                      <!-- Roles 1/2/3 -->
                      <template v-if="userRole !== 4">
                        <template v-if="userRole === 3">
                          <span class="material-icons action-icon"
                                v-if="(ticket.asignado_a === userName || ticket.creado_por === userName)"
                                :title="$t('editar')"
                                @click="openEditModal(ticket)">edit_square</span>

                          <span class="material-icons action-icon"
                                v-if="(ticket.asignado_a === userName || ticket.creado_por === userName)"
                                :title="ticket.estado !== 'Cerrado' ? $t('cerrar_ticket') : $t('reabrir_ticket')"
                                @click="openOpenCloseModal(ticket)">{{ ticket.estado !== 'Cerrado' ? 'lock' : 'lock_open' }}</span>
                        </template>
                        <template v-else>
          <span class="material-icons action-icon"
            :title="ticket.estado === 'Cerrado' ? ($t('ver') || 'Ver') : $t('editar')"
            @click="openEditModal(ticket)">{{ ticket.estado === 'Cerrado' ? 'visibility' : 'edit_square' }}</span>

                          <span class="material-icons action-icon"
                                v-if="ticket.estado !== 'Cerrado'"
                                :title="$t('asignar_usuario')"
                                @click="openAssignUserModal(ticket)">person_add</span>

                          <span class="material-icons action-icon"
                                :title="ticket.estado !== 'Cerrado' ? $t('cerrar_ticket') : $t('reabrir_ticket')"
                                @click="openOpenCloseModal(ticket)">{{ ticket.estado !== 'Cerrado' ? 'lock' : 'lock_open' }}</span>

                          <span class="material-icons action-icon"
                                :title="$t('ver_logs')"
                                @click="openLogModal(ticket)">list_alt</span>

                          <span class="material-icons action-icon delete"
                                v-if="ticket.estado !== 'Cerrado'"
                                :title="$t('eliminar_ticket')"
                                @click="deleteTicket(ticket)">delete</span>
                        </template>
                      </template>

                      <!-- Operarios (role 4): mismo icono de editar; logs ocultos -->
                      <template v-else>
        <span class="material-icons action-icon"
          v-if="(ticket.creado_por_id === userId || ticket.asignado_a_id === userId)"
          :title="ticket.estado === 'Cerrado' ? ($t('ver') || 'Ver') : $t('editar')"
          @click="openEditModal(ticket)">{{ ticket.estado === 'Cerrado' ? 'visibility' : 'edit_square' }}</span>
                        <!-- logs hidden intentionally for operarios -->
                      </template>
                    </template>
                    <template v-else>
                      <span v-if="userRole !== 4" class="material-icons action-icon" :title="$t('ver_logs')" @click="openLogModal(ticket)">list_alt</span>
                      <span class="material-icons action-icon delete" v-if="ticket.estado !== 'Cerrado'" :title="$t('restaurar_ticket')" @click="restoreTicket(ticket)">delete_outline</span>
                    </template>
                   </div>
                </td>
              </tr>

              <!-- Fila extra visible SOLO en móvil con tres campos adicionales -->
              <tr class="mobile-extra-row">
                <td colspan="10">
                  <div class="mobile-extra">
                    <div class="item">
                      <span v-if="!ticket.showFullAsignado">
                        {{ truncateWords(ticket.asignado_a, 2) }}
                        <a v-if="wordCount(ticket.asignado_a) > 2" href="#" @click.prevent="toggleAsignado(ticket)">{{ $t('ver_mas') }}</a>
                      </span>
                      <span v-else>
                        {{ ticket.asignado_a }}
                        <a href="#" @click.prevent="toggleAsignado(ticket)">{{ $t('ver_menos') }}</a>
                      </span>
                    </div>
                    <div class="item">{{ ticket.departamento }} - {{ ticket.delegacion }}</div>
                    <div class="item">{{ ticket.categoria || $t('sin_categoria') || 'Sin categoría' }}</div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <!-- table body ends here -->
        </div>
      </div>

    <!-- Modal para el formulario de ticket -->
    <div v-if="showModal">
      <div class="modal-content">
        <TicketFormModalView 
          @close="closeModal" 
          @submit="handleTicketCreated" 
        />
      </div>
    </div>
    
    <!-- Modal para editar ticket -->
    <div v-if="showEditModal">
      <div class="modal-content">
        <TicketFormModalViewUpdate 
          v-if="showEditModal"
          :ticket="selectedTicket"
          :forceReadOnly="editModeViewOnly"
          :creadoPor="selectedTicket.creado_por"
          @submit="onTicketUpdated"
          @close="onTicketUpdated"
        />
      </div>
    </div>
    
    <!-- Modal para asignar usuario -->
    <div v-if="showAssignUserModal">
      <div class="modal-content">
        <AssignUserModalView
          v-if="showAssignUserModal"
          :ticketId="selectedTicket.id"
          :users="usuarios"
          :currentUserRole="userRole"
          :currentUserId="userId"
          @user-assigned="handleUserAssigned"
          @close="showAssignUserModal = false"
        />
      </div>
    </div>
    
    <!-- Modal para cerrar/reabrir ticket -->
    <teleport to="body" v-if="showOpenCloseModal">
      <div class="no-backdrop-viewport" role="dialog" aria-modal="true">
        <OpenCloseTicketModalView
          :ticketId="selectedTicket?.id"
          :isClosed="selectedTicket?.estado === 'Cerrado'"
          @close="closeOpenCloseModal"
          @submit="handleTicketStateUpdated"
        />
      </div>
    </teleport>
    
    <!-- Modal para ver logs del ticket -->
    <TicketsLogModalView
      v-if="showLogModal && selectedTicket"
      :visible="showLogModal"
      :ticketId="selectedTicket.id"
      :usuarios="usuarios"
      @close="closeLogModal"
      @update:visible="showLogModal = $event"
    />
    <!-- Mensaje de auto-refresh pausado eliminado -->

    <!-- Modal de advertencia de inactividad (no toca sessionStorage) -->
    <div v-if="showIdleWarningModal" class="modal-container open" role="dialog" aria-modal="true">
      <div class="modal-fixed" role="document">
        <div class="red-header">
          <h3 class="modal-title">{{ $t('advertencia_inactividad_title') || 'Inactividad detectada' }}</h3>
          <button class="btn-close" @click="showIdleWarningModal = false">×</button>
        </div>
        <div style="padding:16px;">
          <p>{{ $t('advertencia_inactividad_text') || 'Llevas tiempo sin interactuar. Si no realizas ninguna acción, el auto-refresh se detendrá y la sesión dejará de renovarse.' }}</p>
          <p v-if="idleRemainingMs">{{ Math.ceil(idleRemainingMs / 1000 / 60) }} {{ $t('minutos_restantes') || 'minutos restantes' }}</p>
          <div style="display:flex; gap:8px; justify-content:flex-end; margin-top:12px;">
            <button class="btn btn-secondary" @click="showIdleWarningModal = false">{{ $t('cerrar') || 'Cerrar' }}</button>
            <!-- Botón 'Seguir conectado' eliminado: la renovación ya no se puede forzar desde aquí -->
          </div>
        </div>
      </div>
    </div>
    <!-- spacer to ensure visible gap before the footer -->
    <div class="content-spacer" aria-hidden="true"></div>
  </div>
</template>

<script>
import api from '../api';
import TicketFormModalView from './TicketFormModalView.vue';
import TicketFormModalViewUpdate from './TicketFormModalViewUpdate.vue';
import AssignUserModalView from './AssignUserModalView.vue';
import OpenCloseTicketModalView from './OpenCloseTicketModalView.vue';
import TicketsLogModalView from './TicketsLogModalView.vue';
import Swal from 'sweetalert2';
import { showAlert } from '@/utils/alerts';
import { createInactivityWatcher } from '@/utils/inactivityWatcher';
import { formatDateTime } from '@/utils/formatDate';
import { useAutoRefreshStore } from '@/stores/autoRefresh';

export default {
  name: 'WelcomeView',
  components: {
    TicketFormModalView,
    TicketFormModalViewUpdate,
    AssignUserModalView,
    OpenCloseTicketModalView,
    TicketsLogModalView,
  },
  data() {
    return {
      // enable runtime layout debugging/logging
      debugLayout: false,
      tickets: [],
      filteredTickets: [],
      selectedEstado: "Abiertos y En Proceso",
      selectedCategoria: "",
      selectedBorrado: "no-borrados",
      categorias: [],
      userName: '',
  currentUserData: {},
  showUserDataVisible: false,
      estados: ['Abierto', 'En Proceso', 'Cerrado'],
      showModal: false,
      showEditModal: false,
      showAssignUserModal: false,
      showOpenCloseModal: false,
      showLogModal: false,
      selectedTicket: null,
  // Cuando se abre un ticket cerrado, mostramos el modal en modo solo lectura
  editModeViewOnly: false,
      userRole: null,
      selectedAsignado: '',
      usuarios: [],
      usuario: null,
      userId: null,
  // Manager para autorefresh global (se inicializa en mounted). Mantener referencias locales para compatibilidad de logs
  autoRefreshManager: null,
  // Timer id local (deprecated) — mantenido para compatibilidad
  autoRefreshTimer: null,
  // Instance counter local (diagnóstico)
  autoRefreshInstance: 0,
  // Timeout id local (deprecated)
  lastRefreshTimeoutId: null,
  // Flag local para proteger el timeout final de cancelaciones por actividad incidental
  lastRefreshProtected: false,
  // Flag local que indica que ya hay un último refresco programado (evita reinicios automáticos)
  finalScheduled: false,
  // Contador de cuántas veces se ha refrescado la vista (diagnóstico)
  refreshCount: 0,
  // Marca para permitir sólo la carga inicial en rol 4 (evitar refresh posteriores)
  loadedOnce: false,
  // Tope configurable de refrescos automáticos consecutivos (definitivo)
  maxAutoRefresh: 85,
  // Intervalo de auto-refresh en milisegundos -- 2 minutos
  autoRefreshIntervalMs: 2 * 60 * 1000,
  // Delay final para el último refresco (ms): 10 minutos + 5 segundos (definitivo)
  finalDelayMs: (10 * 60 * 1000) + 5000,
  // Inactivity detection state (no changes to sessionStorage)
  showIdleWarningModal: false,
  idleRemainingMs: 0,
  inactivityWatcher: null,
  // Estado para controlar qué fila tiene abierto el menú de acciones en móvil
  openActionsFor: null,
      categoriaNombreAClave: {
        "Hardware": "hardware",
        "Software": "software",
        "Redes y conectividad": "redes",
        "Fichaje": "fichaje",
        "Correo Electronico": "correo",
        "Acceso y contraseñas": "acceso",
        "Impresion y escaneo": "impresion",
        "Seguridad": "seguridad",
        "Telefonia": "telefonia",
        "Aplicaciones Web / Intranet": "web",
        "Otros": "otros"
      },
  // Campo unificado de búsqueda: puede ser un número de ticket o texto para la descripción
  searchQuery: '',
      // Controles para colapsar/expandir filtros en móvil
      filtersCollapsed: false,
      // Flag que indica si la vista actual es móvil (<=576px)
      isMobile: false,
    };
  },
  computed: {
    nombresAsignados() {
      if (this.userRole === 2) {
        const tecnicosSupervisados = this.usuarios
          .filter(u => u.rol_id === 3 && u.responsable_id === this.userId)
          .map(u => u.nombre_completo);

        const nombreSupervisor = this.userName;
        return [...new Set([nombreSupervisor, ...tecnicosSupervisados])];
      } else {
        const nombres = this.tickets
          .map(t => t.asignado_a)
          .filter(n => n && n.trim() !== '' && n !== 'Sin asignar');
        return [...new Set(nombres)];
      }
    },
    userDataArray() {
      try {
        const obj = this.currentUserData || {};
        return Object.keys(obj).map(k => ({ key: k, value: obj[k] }));
      } catch (e) {
        return [];
      }
    },
  },
  async created() {
    try {
      const userResponse = await api.get('/user');
      this.currentUserData = userResponse.data || {};
      this.userName = userResponse.data.nombre_completo;
      this.userRole = userResponse.data.rol_id;
      this.userId = userResponse.data.id;

      // Reiniciar contador y estado de carga al obtener el usuario
      try {
        this.refreshCount = 0;
        this.loadedOnce = false;
        if (this.debugLayout) console.info('[WelcomeView] contador reiniciado tras created() —', (new Date()).toLocaleString());
      } catch (e) {}

      await this.loadTickets();
      await this.loadCategorias();
      // No cargar el listado completo de usuarios para técnicos (rol 3) — evita 403 al llamar /users
      try {
        if (this.userRole !== 3) {
          await this.loadUsuarios();
        } else {
          if (this.debugLayout) console.info('[WelcomeView] loadUsuarios omitido para rol 3 (técnico)');
          this.usuarios = [];
        }
      } catch (e) {
        console.warn('[WelcomeView] loadUsuarios falló o fue omitido:', e);
      }
    } catch (err) {
      console.error('Error al obtener el usuario, los tickets o las categorías:', err.response?.data || err.message);
    }
  },
  async mounted() {
    try {
      await this.loadDatos();
      // Inicializar estado de filtros según ancho inicial
      this.updateFiltersCollapsedOnResize();
      // inicializar bandera isMobile y registrar listener
      this.updateIsMobile();
      window.addEventListener('resize', this.updateFiltersCollapsedOnResize);
      window.addEventListener('resize', this.updateIsMobile);
      // calcular y fijar la segunda fila de títulos justo debajo de la primera
      this.$nextTick(() => {
        this.computeSecondHeaderTop();
        // retry shortly after mount to handle slow rendering on mobile
        setTimeout(() => { try { this.computeSecondHeaderTop(); } catch (e) {} }, 80);
      });
      window.addEventListener('resize', this.computeSecondHeaderTop);
      // Ensure the table container height is adjusted on mount/resize so only tbody scrolls on desktop
      try {
        this.$nextTick(() => {
          try { this.computeDesktopTableMaxHeight(); } catch (e) {}
        });
      } catch (e) {}
      window.addEventListener('resize', this.computeDesktopTableMaxHeight);
      // also recalculate when the table container scrolls (useful on mobile where the table is scrollable)
      try {
        const tableContainer = this.$el.querySelector('.table-container');
        if (tableContainer) {
          tableContainer.addEventListener('scroll', this.computeSecondHeaderTop);
        }
      } catch (e) {}
      // aplicar alternancia de filas tras render inicial
      this.$nextTick(() => { try { this.applyRowStriping(); } catch (e) {} });
      // Prevent the document from scrolling so only .table-container scrolls
      try {
        // inject a style with !important to override other CSS that may re-enable scrolling
        const existing = document.getElementById('welcome-no-scroll');
        if (!existing) {
          const style = document.createElement('style');
          style.id = 'welcome-no-scroll';
          style.innerHTML = 'html, body, #app { overflow: hidden !important; }';
          document.head.appendChild(style);
        }
      } catch (e) {}
    } catch (error) {
      return;
    }

  // Diagnostic: enable debug logging and show instance id for this run
  try { this.debugLayout = true; console.info('[WelcomeView] mounted uid=', this._uid); } catch (e) {}

    // Auto-refresh: inicializar o unirse al gestor global para todos los usuarios excepto el operario (rol 4)
    try {
      // generar id local único para esta instancia (si no existe)
      try { this._welcomeUid = this._welcomeUid || Math.random().toString(36).substr(2,9); } catch (e) { this._welcomeUid = String(Math.random()); }

      try {
        // Usar store Pinia para el gestor global
        try {
          this.autoRefreshManager = useAutoRefreshStore();
          this.autoRefreshManager.incrementInstances();
          if (this.debugLayout) console.info('[WelcomeView] autoRefreshManager activeInstances=', this.autoRefreshManager.activeInstances);
          if (this.userRole !== 4 && this.autoRefreshManager.activeInstances === 1) {
            this.ensureAutoRefreshRunning();
          }
        } catch (e) {
          console.error('[WelcomeView] no se pudo inicializar autoRefreshManager (Pinia)', e);
        }
      } catch (e) {
        // noop
      }
    } catch (e) {}

    // Listener global: forzar refresco cuando se cree un ticket en otro componente
    try {
      this._onTicketCreatedGlobal = async (ev) => {
        try {
          console.info('[WelcomeView] evento ticketCreated recibido, forzando loadTickets()');
          await this.loadTickets();
          this.filterTickets();
        } catch (e) { console.error('Error al manejar ticketCreated:', e); }
      };
      window.addEventListener('ticketCreated', this._onTicketCreatedGlobal);
    } catch (e) {}

    // Inicializar watcher de inactividad (no modifica sessionStorage)
    try {
      // crear watcher reutilizable
      this.inactivityWatcher = createInactivityWatcher({
        inactivityLimitMs: 3 * 60 * 60 * 1000,
        inactivityWarningBeforeMs: 5 * 60 * 1000,
        onWarning: ({ remainingMs }) => {
          this.idleRemainingMs = remainingMs || 0;
          this.showIdleWarningModal = true;
        },
        onExpired: () => {
          this.showIdleWarningModal = false;
          // pause auto-refresh
          if (this.autoRefreshTimer) { clearInterval(this.autoRefreshTimer); this.autoRefreshTimer = null; }
        },
        onActivity: () => {
          // El usuario ha realizado alguna acción: manejar el timeout final con cuidado
          this.showIdleWarningModal = false;
          try {
            if (this.lastRefreshTimeoutId) {
              // Si el último refresco está protegido, no lo cancelamos por actividad incidental
              if (this.lastRefreshProtected) {
                if (this.debugLayout) console.info('[WelcomeView] Actividad detectada pero último refresco protegido: no se cancela');
              } else {
                try {
                  const attemptNum = Number(this.refreshCount || 0) + 1;
                  const totalNum = Number(this.maxAutoRefresh || 2) + 1;
                  console.info(`[WelcomeView] Actividad detectada: cancelando último refresco programado — intento ${attemptNum} de ${totalNum}`);
                } catch (e) {
                  console.info('[WelcomeView] Actividad detectada: cancelando último refresco programado');
                }
                try { clearTimeout(this.lastRefreshTimeoutId); } catch (e) {}
                this.lastRefreshTimeoutId = null;
                this.lastRefreshProtected = false;
                this.finalScheduled = false;
              }
            }
          } catch (e) {}
          // Reiniciar contador de refrescos y arrancar auto-refresh sólo si NO hay un último refresco programado
          try {
            if (!this.lastRefreshTimeoutId && !this.finalScheduled) {
              this.refreshCount = 0;
              if (this.userRole !== 4) this.ensureAutoRefreshRunning();
            } else {
              if (this.debugLayout) console.info('[WelcomeView] Actividad detectada pero hay un último refresco pendiente: no reiniciamos auto-refresh');
            }
          } catch (e) {}
        }
      });
      this.inactivityWatcher.start();
    } catch (e) {
      console.error('No se pudo iniciar el watcher de inactividad:', e);
    }
  },
  updated() {
    // reaplicar alternancia cuando el DOM cambie (tickets filtrados, paginación, etc.)
    try { this.applyRowStriping(); } catch (e) {}
    try { this.computeDesktopTableMaxHeight(); } catch (e) {}
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateFiltersCollapsedOnResize);
    try { window.removeEventListener('resize', this.computeSecondHeaderTop); } catch (e) {}
    try { window.removeEventListener('resize', this.updateIsMobile); } catch (e) {}
    try { window.removeEventListener('resize', this.computeDesktopTableMaxHeight); } catch (e) {}
    try {
      const tableContainer = this.$el && this.$el.querySelector ? this.$el.querySelector('.table-container') : null;
      if (tableContainer) {
        tableContainer.removeEventListener('scroll', this.computeSecondHeaderTop);
      }
    } catch (e) {}
    // Limpiar listener global ticketCreated
    try { window.removeEventListener('ticketCreated', this._onTicketCreatedGlobal); } catch (e) {}
    // Restore document scrolling
    try {
      const s = document.getElementById('welcome-no-scroll');
      if (s && s.parentNode) s.parentNode.removeChild(s);
      // also attempt to remove inline styles if present
      try { document.documentElement.style.removeProperty('overflow'); document.body.style.removeProperty('overflow'); } catch (e) {}
    } catch (e) {}

    // Limpiar watcher de inactividad
    try {
      if (this.inactivityWatcher && this.inactivityWatcher.stop) this.inactivityWatcher.stop();
    } catch (e) {}

    // Desregistrar esta instancia del gestor Pinia y limpiar timers si ya no quedan instancias
    try {
      if (this.autoRefreshManager) {
        this.autoRefreshManager.decrementInstances();
        if (this.debugLayout) console.info('[WelcomeView] autoRefreshManager activeInstances decremented=', this.autoRefreshManager.activeInstances);
        if (this.autoRefreshManager.activeInstances === 0) {
          // limpiar intervalos y timeouts globales
          try { this.autoRefreshManager.clearTimer(); } catch (e) {}
          try { this.autoRefreshManager.clearLastTimeout(); } catch (e) {}
          this.autoRefreshManager.finalScheduled = false;
          this.autoRefreshManager.lastProtected = false;
          this.autoRefreshManager.refreshCount = 0;
        }
      }
    } catch (e) {}

    // Log final: cuántas veces se refrescó la vista durante la sesión (solo en memoria)
    try {
      const finalCount = this.refreshCount || 0;
      console.info(`[WelcomeView] beforeUnmount — refrescos totales durante la sesión: ${finalCount}`);
    } catch (e) {}
  },
  methods: {
    // Normaliza texto: quita acentos y pasa a minúsculas
    normalizeText(str) {
      if (!str) return '';
      return String(str).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    },
    mapPriority(priorityName) {
      switch (priorityName) {
        case 'Alta':
          return 3;
        case 'Media':
          return 2;
        case 'Baja':
          return 1;
        default:
          return 0;
      }
    },
  async loadTickets(isAuto = false, opts = {}) {
      // Si ya se cargaron una vez y el usuario es operario (rol 4), evitar refrescos posteriores
      try {
        if (this.userRole === 4 && this.loadedOnce) {
          if (this.debugLayout) console.debug('[WelcomeView] loadTickets omitido para rol 4 tras carga inicial');
          return;
        }
      } catch (e) {}
      try {
        const response = await api.get('/tickets');

        this.tickets = response.data.map((ticket) => ({
          id: ticket.id,
          descripcion: ticket.descripcion,
          estado: ticket.ticket_estado?.nombre || 'Sin estado',
          prioridad: ticket.ticket_prioridad?.nombre || 'Sin prioridad',
          prioridad_id: ticket.ticket_prioridad?.id || null,
          prioridad_valor: this.mapPriority(ticket.ticket_prioridad?.nombre),
          delegacion: ticket.delegacion?.nombre || 'Sin delegación',
          departamento: ticket.departamento?.nombre || 'Sin departamento',
          categoria: ticket.ticket_categoria?.nombre || 'Sin categoría',
          categoria_id: ticket.ticket_categoria?.id || null,
          creado_por: ticket.usuario?.nombre_completo || 'Desconocido',
          creado_por_id: ticket.usuario?.id || null,
          asignado_a: ticket.usuario_asignado?.nombre_completo || 'Sin asignar',
          asignado_a_id: ticket.usuario_asignado?.id || null,
          registrado_id: ticket.registrado_id || null,
          fecha_hora: ticket.fecha_creacion || 'Sin fecha',
          borrado: ticket.borrado,
          tiene_adjuntos: ticket.tiene_adjuntos,
          showFullDescription: false,
          showFullCreado: false,
          showFullAsignado: false,
        }));

        this.tickets.sort((a, b) => {
          if (b.prioridad_valor !== a.prioridad_valor) {
            return b.prioridad_valor - a.prioridad_valor;
          }
          const dateA = new Date(a.fecha_hora);
          const dateB = new Date(b.fecha_hora);
          return dateB - dateA;
        });

        this.filteredTickets = this.tickets;
          this.filterTickets();
          this.$nextTick(() => { try { this.applyRowStriping(); } catch (e) {} });
        // Asegurar que la segunda fila calcule su top después de renderizar los tickets
        this.$nextTick(() => {
          try { this.computeSecondHeaderTop(); } catch (e) {}
          // retry shortly after to avoid race conditions on some devices
          setTimeout(() => { try { this.computeSecondHeaderTop(); } catch (e) {} }, 60);
        });

        // Contabilizar SOLO los refrescos automáticos (llamados desde setInterval).
        // NO aumentar para operarios (rol 4) — no se debe refrescar para esos usuarios
        try {
          const timestamp = (new Date()).toLocaleString();
          if (this.userRole !== 4) {
            if (isAuto) {
              // Solo contar si la llamada proviene del gestor global (evita duplicados)
              if (opts && opts.fromManager && this.autoRefreshManager) {
                try { this.autoRefreshManager.refreshCount = (Number(this.autoRefreshManager.refreshCount) || 0) + 1; } catch (e) { this.autoRefreshManager.refreshCount = 1; }
                // espejo local para logs/UI
                try { this.refreshCount = this.autoRefreshManager.refreshCount; } catch (e) { this.refreshCount = this.refreshCount || 0; }
                console.info(`[WelcomeView] loadTickets llamado — auto-refresh intento ${this.refreshCount} de ${this.maxAutoRefresh || 2} — ${timestamp}`);
              } else {
                if (this.debugLayout) console.info('[WelcomeView] loadTickets auto llamdo pero no contado (no fromManager)');
              }
              // Si alcanzamos el límite de refrescos consecutivos, detener el auto-refresh
              try {
                if (Number(this.autoRefreshManager.refreshCount) >= Number(this.maxAutoRefresh || 2)) {
                  // Detener el timer global
                  try { if (this.autoRefreshManager.timerId) { clearInterval(this.autoRefreshManager.timerId); this.autoRefreshManager.timerId = null; } } catch (e) {}
                  console.info(`[WelcomeView] Límite alcanzado — refresco ${this.autoRefreshManager.refreshCount} de ${this.maxAutoRefresh} — auto-refresh detenido`);
                  try { this.showToast(this.$t?.('auto_refresh_limit_reached') || `Auto-refresh detenido tras ${this.maxAutoRefresh} refrescos`, 'info'); } catch (e) {}
                    // Programar un último refresco único a los 10 minutos + 5 segundos
                    try {
                      const finalDelayMs = Number(this.finalDelayMs || ((10 * 60 * 1000) + 5000));
                        if (!this.autoRefreshManager.lastTimeoutId && !this.autoRefreshManager.finalScheduled) {
                          try { const attemptNum = Number(this.autoRefreshManager.refreshCount || 0) + 1; const totalNum = Number(this.maxAutoRefresh || 2) + 1; console.info(`[WelcomeView] Programado último refresco único en ${Math.round(finalDelayMs/1000)}s — intento ${attemptNum} de ${totalNum}`); } catch (e) {}
                          // Protegemos este timeout final en el gestor global para que no sea cancelado por actividad incidental
                          this.autoRefreshManager.lastProtected = true;
                          this.autoRefreshManager.finalScheduled = true;
                          this.autoRefreshManager.lastTimeoutId = setTimeout(async () => {
                            try {
                              console.info(`[WelcomeView] Ejecutando último refresco programado (inicio)`);
                              // Ejecutar la carga (marcar isAuto true para contabilizar desde el gestor)
                              await this.loadTickets(true, { fromManager: true });
                              console.info(`[WelcomeView] Ejecutado último refresco programado (completado) — refreshCount=${this.autoRefreshManager.refreshCount}`);
                            } catch (err) {
                              console.error('Error en último refresco programado:', err);
                            } finally {
                              this.autoRefreshManager.lastTimeoutId = null;
                              this.autoRefreshManager.lastProtected = false;
                              this.autoRefreshManager.finalScheduled = false;
                              // espejo local
                              try { this.lastRefreshTimeoutId = null; this.lastRefreshProtected = false; this.finalScheduled = false; } catch (e) {}
                            }
                          }, finalDelayMs);
                        } else {
                          if (this.debugLayout) console.info('[WelcomeView] Último refresco ya programado o marcado, evitando duplicado');
                        }
                    } catch (e) {}
                }
              } catch (e) {}
            } else {
              // llamada manual / forzada por el usuario o por otras acciones internas
              console.info(`[WelcomeView] loadTickets llamado — manual — ${timestamp}`);
            }
          } else {
            // Para rol 4 no incrementamos el contador, pero siempre dejamos constancia en la consola
            console.info(`[WelcomeView] loadTickets llamado — rol 4 (no se incrementa contador) — ${timestamp}`);
            if (this.debugLayout) console.debug('[WelcomeView] loadTickets llamado — omitiendo contador para rol 4', { timestamp });
          }
        } catch (e) {
          // noop: si sessionStorage no está disponible, ignorar
        }
        // marcar que se ha realizado al menos una carga satisfactoria
        try { this.loadedOnce = true; } catch (e) {}
      } catch (err) {
        console.error('Error al cargar los tickets:', err.response?.data || err.message);
      }
    },

    async loadCategorias() {
      try {
        const response = await api.get('/tickets/categorias');
        this.categorias = response.data.categorias.map((categoria) => ({
          id: categoria.id,
          nombre: categoria.nombre,
        }));
      } catch (err) {
        console.error('Error al cargar las categorías:', err.response?.data || err.message);
      }
    },
    async loadUsuarios() {
      try {
        // Protección adicional: si por alguna razón se llama y el usuario es técnico, omitir la petición
        if (this.userRole === 3) {
          if (this.debugLayout) console.info('[WelcomeView] loadUsuarios() early return para rol 3 (técnico)');
          this.usuarios = [];
          return;
        }

        const response = await api.get('/users');
        this.usuarios = response.data.data.map(u => ({
          ...u,
          rol_id: u.rol && typeof u.rol === 'object' ? Number(u.rol.id) : Number(u.rol_id),
          responsable_id: u.usuario_responsable && typeof u.usuario_responsable === 'object'
            ? Number(u.usuario_responsable.id)
            : null
        }));
      } catch (err) {
        console.error('Error al cargar los usuarios:', err.response?.data || err.message);
        // En caso de 403 u otro error, asegurar que usuarios queda al menos como array vacío
        try { this.usuarios = this.usuarios || []; } catch (e) {}
      }
    },
    getTicketsBase() {
      return [...this.tickets];
    },
    filterTickets() {
      let resultado = this.getTicketsBase();

      // Nota: la visibilidad por rol (quién puede ver cada ticket) la determina
      // el backend. Eliminamos la lógica de restricción en el frontend para
      // evitar duplicidad. Aquí sólo aplicamos filtros de presentación/UX.

      // Búsqueda por texto en la descripción (ignorar mayúsculas y acentos)
      // Si hay texto de búsqueda, decidir si es un número (buscar por id) o texto (buscar en descripción)
      if (this.searchQuery && String(this.searchQuery).trim() !== '') {
        const raw = String(this.searchQuery).trim();
        const onlyDigits = /^\d+$/.test(raw);
        if (onlyDigits) {
          // búsqueda por número de ticket
          resultado = resultado.filter(ticket => String(ticket.id) === raw);
        } else {
            // búsqueda por texto: normalizada en descripción y en el nombre del creador (creado_por)
            const q = this.normalizeText(raw);
            resultado = resultado.filter(ticket => {
              const descripcion = this.normalizeText(ticket.descripcion || '');
              const creadoPor = this.normalizeText(ticket.creado_por || '');
              return descripcion.includes(q) || creadoPor.includes(q);
            });
        }
      }
      if (this.selectedEstado === "Abiertos y En Proceso") {
        resultado = resultado.filter(
          (ticket) => ticket.estado === "Abierto" || ticket.estado === "En Proceso"
        );
      } else if (this.selectedEstado) {
        resultado = resultado.filter(
          (ticket) => ticket.estado === this.selectedEstado
        );
      }
      if (this.selectedCategoria) {
        resultado = resultado.filter(
          (ticket) => ticket.categoria === this.selectedCategoria
        );
      }
      if (this.selectedBorrado === "borrados") {
        resultado = resultado.filter(ticket => ticket.borrado === 1 || ticket.borrado === true);
      } else if (this.selectedBorrado === "no-borrados") {
        resultado = resultado.filter(ticket => ticket.borrado === 0 || ticket.borrado === false);
      }
      if (this.selectedAsignado === "Sin asignar") {
        resultado = resultado.filter(ticket => !ticket.asignado_a_id);
      } else if (this.selectedAsignado) {
        resultado = resultado.filter(ticket => ticket.asignado_a === this.selectedAsignado);
      }

      resultado.sort((a, b) => {
        if (b.prioridad_valor !== a.prioridad_valor) {
          return b.prioridad_valor - a.prioridad_valor;
        }
        const dateA = new Date(a.fecha_hora);
        const dateB = new Date(b.fecha_hora);
        return dateB - dateA;
      });

      this.filteredTickets = resultado;
      this.$nextTick(() => { try { this.applyRowStriping(); } catch (e) {} });
    },
    openTicketForm() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    getPriorityClass(priorityValue) {
      if (priorityValue === 4) return 'priority-critical';
      if (priorityValue === 3) return 'priority-high';
      if (priorityValue === 2) return 'priority-medium';
      if (priorityValue === 1) return 'priority-low';
      return '';
    },
    async handleTicketCreated(newTicket) {
      await this.loadTickets();
    },
    async openEditModal(ticket) {
      try {
        const response = await api.get(`/tickets/${ticket.id}`);

        this.selectedTicket = {
          ...response.data.ticket,
          categoria: response.data.ticket.categoria_id,
          prioridad: response.data.ticket.prioridad_id,
          descripcion: response.data.ticket.descripcion,
          creado_por: ticket.creado_por || response.data.ticket.usuario?.nombre_completo || 'Desconocido',
        };

        // Si el ticket está cerrado, forzamos modo de solo consulta
        try { this.editModeViewOnly = ((response.data.ticket.estado || ticket.estado) === 'Cerrado'); } catch (e) { this.editModeViewOnly = false; }
        this.showEditModal = true;
      } catch (error) {
        console.error('Error al obtener los datos del ticket:', error.response?.data || error.message);
      }
    },
    async onTicketUpdated() {
      await this.loadTickets();
      this.filterTickets();
      setTimeout(() => {
        this.showEditModal = false;
        this.selectedTicket = null;
      }, 200);
    },
    closeEditModal() {
      this.showEditModal = false;
      this.selectedTicket = null;
    },
    openAssignUserModal(ticket) {
      this.selectedTicket = ticket;
      this.showAssignUserModal = true;
    },
    closeAssignUserModal() {
      this.showAssignUserModal = false;
      this.selectedTicket = null;
    },
    async handleUserAssigned(updatedTicket) {
      try {
        const ticketId = updatedTicket.id || (this.selectedTicket && this.selectedTicket.id) || 'desconocido';
        const tecnico = updatedTicket.tecnico_nombre || updatedTicket.tecnico?.nombre_completo || updatedTicket.usuario_asignado_nombre || 'desconocido';
        Swal.close();
        await showAlert('success', this.$t('tecnico_asignado_title') || 'Asignado', this.$t('tecnico_asignado_text',{ tecnico, id: ticketId }));
        await this.loadTickets();
        this.closeAssignUserModal();
      } catch (error) {
        console.error('Error al asignar el usuario:', error.response?.data || error.message);
        await Swal.fire({
          title: this.$t('sweetalert_error_title'),
          text: this.$t('tecnico_asignado_error'),
          icon: 'error',
          confirmButtonColor: '#dc3545',
          cancelButtonColor: '#6c757d',
          customClass: { popup: 'swal2-center' },
        });
      }
    },
    openOpenCloseModal(ticket) {
      this.selectedTicket = ticket;
      this.showOpenCloseModal = true;
    },
    closeOpenCloseModal() {
      this.showOpenCloseModal = false;
      this.selectedTicket = null;
    },
    async handleTicketStateUpdated(updatedTicket) {
      try {
        this.closeOpenCloseModal();

        await this.$nextTick();

        await showAlert(
          'success',
          this.$t('swal_estado_actualizado') || 'Estado actualizado',
          this.$t('swal_estado_actualizado_text', { id: updatedTicket.id })
        );

        await this.loadTickets();
      } catch (error) {
        console.error('Error al actualizar el estado del ticket:', error.response?.data || error.message);
        await Swal.fire({
          title: this.$t('sweetalert_error_title'),
          html: this.$t('sweetalert_ticket_error', { id: updatedTicket?.id || 'desconocido' }),
          icon: 'error',
          confirmButtonColor: '#dc3545',
        });
      }
    },
    openLogModal(ticket) {
      this.selectedTicket = ticket;
      this.showLogModal = true;
    },
    closeLogModal() {
      this.showLogModal = false;
      this.selectedTicket = null;
    },
    async deleteTicket(ticket) {
      const result = await Swal.fire({
        title: this.$t('confirmar_eliminar', { id: ticket.id }),
        text: this.$t('confirmar_eliminar_text'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('si_eliminar'),
        cancelButtonText: this.$t('cancelar'),
        confirmButtonColor: '#99172A',
        cancelButtonColor: '#6c757d',
      });

      if (result.isConfirmed) {
        try {
          await api.delete(`/tickets/${ticket.id}`);

          await Swal.fire({
            title: this.$t('eliminado'),
            text: this.$t('eliminar_exito_text', { id: ticket.id }),
            icon: 'success',
            confirmButtonColor: '#99172A',
            cancelButtonColor: '#6c757d',
          });
          await this.loadTickets();
        } catch (error) {
          await Swal.fire({
            title: this.$t('sweetalert_error_title'),
            text: this.$t('error_eliminar_ticket'),
            icon: 'error',
            confirmButtonColor: '#99172A',
            cancelButtonColor: '#6c757d',
          });
        }
      }
    },
    async handleAction() {
      try {
        const endpoint = this.isClosed
          ? `/tickets/${this.ticketId}/reopen`
          : `/tickets/${this.ticketId}/close`;

        const response = await api.put(endpoint, {});

        this.$emit('submit', { id: this.ticketId, ...response.data });
      } catch (error) {
        console.error('Error al actualizar el estado del ticket:', error.response?.data || error.message);
      } finally {
        this.$emit('close');
      }
    },
    truncateText(text, maxLength) {
      if (!text) return '';
      const s = String(text);
      if (s.length > maxLength) {
        return s.substring(0, maxLength) + '...';
      }
      return s;
    },
    truncateWords(text, maxWords) {
      if (!text) return '';
      const words = text.split(' ');
      if (words.length > maxWords) {
        return words.slice(0, maxWords).join(' ') + '...';
      }
      return text;
    },
    formatFechaHora(value) {
      return formatDateTime(value);
    },
    wordCount(text) {
      if (!text) return 0;
      return text.trim().split(/\s+/).length;
    },
    toggleCreado(ticket) {
      ticket.showFullCreado = !ticket.showFullCreado;
    },
    toggleAsignado(ticket) {
      ticket.showFullAsignado = !ticket.showFullAsignado;
    },
    toggleDescription(ticket) {
      ticket.showFullDescription = !ticket.showFullDescription;
    },

    // Return the first `lines` lines of text as HTML (escaped + <br>) for safe rendering
    previewByLines(text, lines = 2) {
      if (!text) return '';
      const rawLines = String(text).split(/\r?\n/);
      const chosen = rawLines.slice(0, lines);
      // escape HTML to avoid XSS, then join with <br>
      return chosen.map(l => this.escapeHtml(l)).join('<br>');
    },

    // Return true if the text has more than `lines` lines
    hasMoreLines(text, lines = 2) {
      if (!text) return false;
      return String(text).split(/\r?\n/).length > lines;
    },

    // Minimal HTML escaper
    escapeHtml(unsafe) {
      return String(unsafe)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },
    async restoreTicket(ticket) {
      const result = await Swal.fire({
        title: this.$t('confirmar_restaurar', { nombre: `ticket ${ticket.id}` }),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('si_restaurar'),
        cancelButtonText: this.$t('cancelar'),
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#6c757d',
      });
      if (result.isConfirmed) {
        try {
          await api.put(`/tickets/restaurar/${ticket.id}`);
          await Swal.fire({
            title: this.$t('registro_restaurado'),
            icon: 'success',
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
          });
          await this.loadTickets();
          this.filterTickets();
        } catch (error) {
          await Swal.fire({
            title: this.$t('swal_error'),
            text: error.response?.data?.message || 'Error al restaurar el ticket.',
            icon: 'error',
            confirmButtonColor: '#99172A',
            cancelButtonColor: '#6c757d',
          });
        }
      }
    },
    toggleActions(ticket) {
      if (this.openActionsFor === ticket.id) {
        this.openActionsFor = null;
      } else {
        this.openActionsFor = ticket.id;
      }
    },
    toggleFilters() {
      // Solo permitir alternar filtros en móvil. En escritorio los filtros deben estar siempre visibles.
      if (this.isMobile) {
        this.filtersCollapsed = !this.filtersCollapsed;
      }
    },
    updateFiltersCollapsedOnResize() {
      // En pantallas pequeñas (<=576px) colapsar por defecto; en pantallas grandes mostrar
      if (window.innerWidth <= 576) {
        this.filtersCollapsed = true;
      } else {
        // En pantallas de escritorio garantizar que los filtros estén siempre visibles
        this.filtersCollapsed = false;
      }
    },
    updateIsMobile() {
      // Actualizar la bandera isMobile basada en el ancho de la ventana
      try {
        this.isMobile = window.innerWidth <= 576;
      } catch (e) {
        this.isMobile = false;
      }
      // Si no es móvil, asegurarse de que los filtros estén visibles
      if (!this.isMobile) this.filtersCollapsed = false;
    },

    // Desktop: measure available vertical space and set max-height on .table-container so only tbody scrolls
    computeDesktopTableMaxHeight(attempt = 0) {
      try {
        // Desktop-specific behavior: measure available vertical space and set max-height
        // For mobile widths, ensure the scrollable container has bottom padding matching footer
        if (window.innerWidth <= 768) {
          const tableContainerMobile = this.$el && this.$el.querySelector ? this.$el.querySelector('.table-container') : null;
          // try to find footer and compute its height
          let footerElMobile = document.querySelector('footer.app-footer') || document.querySelector('footer');
          const footerHeightMobile = footerElMobile ? Math.ceil((footerElMobile.getBoundingClientRect().height) || 0) : 0;
          const gapMobile = 24; // small gap so last row isn't flush against footer
          try {
            if (tableContainerMobile) {
              tableContainerMobile.style.paddingBottom = (footerHeightMobile + gapMobile) + 'px';
              // ensure it can scroll
              tableContainerMobile.style.overflowY = 'auto';
            }
            // If we previously set body padding-bottom for desktop, remove it on mobile
            try { document.body.style.removeProperty('padding-bottom'); } catch (e) {}
            if (this.debugLayout) console.debug('[layout] mobile width detected — applied bottom padding to table-container', { footerHeightMobile, gapMobile });
          } catch (e) {}
          return;
        }

        if (!this.$el) return;
        const container = this.$el;
        const tableContainer = container.querySelector('.table-container');
        const headerEl = container.querySelector('.table-header');
        // try to find footer in the document (component may render in layout)
        let footerEl = document.querySelector('footer.app-footer') || document.querySelector('footer');

        // If footer not found yet, retry a few times (it may mount after this view)
        if (!footerEl && attempt < 3) {
          if (this.debugLayout) console.debug(`[layout] footer not found, retrying computeDesktopTableMaxHeight (attempt ${attempt + 1})`);
          setTimeout(() => this.computeDesktopTableMaxHeight(attempt + 1), 150);
          return;
        }

        const headerHeight = headerEl ? Math.ceil(headerEl.getBoundingClientRect().height) : 0;
        const footerHeight = footerEl ? Math.ceil(footerEl.getBoundingClientRect().height) : 0;
        const topOffset = Math.max(0, Math.ceil(container.getBoundingClientRect().top));

  // desired gap between table bottom and footer top
  const gap = 24; // px (reduced to bring table closer to footer)

        let available = window.innerHeight - headerHeight - footerHeight - topOffset - gap;
        if (available < 220) available = 220;

        if (this.debugLayout) {
          console.debug('[layout] computeDesktopTableMaxHeight', { headerHeight, footerHeight, topOffset, gap, available, windowInnerHeight: window.innerHeight });
          if (footerEl) console.debug('[layout] footer position:', getComputedStyle(footerEl).position);
        }

        // If footer is fixed/absolute (overlays content), prefer adding body padding-bottom to prevent overlap
        if (footerEl) {
          const fStyle = getComputedStyle(footerEl);
          if (fStyle.position === 'fixed' || fStyle.position === 'absolute' || fStyle.position === 'sticky') {
            // ensure body has padding-bottom equal to footer height + gap
            try {
              document.body.style.paddingBottom = (footerHeight + gap) + 'px';
              if (this.debugLayout) console.debug('[layout] applied body padding-bottom to accommodate fixed footer:', document.body.style.paddingBottom);
            } catch (e) {}
          } else {
            // remove any previously applied padding-bottom
            try { document.body.style.removeProperty('padding-bottom'); } catch (e) {}
          }
        }

        if (tableContainer) {
          tableContainer.style.maxHeight = available + 'px';
          tableContainer.style.overflowY = 'auto';
          if (this.debugLayout) tableContainer.style.outline = '1px dashed rgba(0,0,0,0.12)';
        }
        // Also set the root welcome container height so it fits exactly between header and footer
        try {
          const containerFullHeight = Math.max(200, window.innerHeight - headerHeight - footerHeight);
          if (this.$el) {
            this.$el.style.height = containerFullHeight + 'px';
            this.$el.style.overflow = 'hidden';
          }
          // Ensure document doesn't scroll (override globals)
          try { document.documentElement.style.overflow = 'hidden'; document.body.style.overflow = 'hidden'; } catch (e) {}
        } catch (e) {}
      } catch (e) {
        if (this.debugLayout) console.error('[layout] computeDesktopTableMaxHeight failed', e);
      }
    },
    
    handleMobileEdit(ticket) {
      this.openActionsFor = null;
      this.openEditModal(ticket);
    },
    handleMobileAssign(ticket) {
      this.openActionsFor = null;
      this.openAssignUserModal(ticket);
    },
    handleMobileLock(ticket) {
      this.openActionsFor = null;
      this.openOpenCloseModal(ticket);
    },
    handleMobileLogs(ticket) {
      this.openActionsFor = null;
      this.openLogModal(ticket);
    },
    async handleMobileDelete(ticket) {
      this.openActionsFor = null;
      await this.deleteTicket(ticket);
    },
    async handleMobileRestore(ticket) {
      this.openActionsFor = null;
      await this.restoreTicket(ticket);
    },
    async loadDatos() {
      // Método para cargar datos adicionales si es necesario
    },
    computeSecondHeaderTop() {
      try {
        if (!this.$el) return;
        const table = this.$el.querySelector('.user-table');
        if (!table) return;
        const firstRow = table.querySelector('thead tr:first-child');
        // Use the mobile header row (second visual line)
        const secondRow = table.querySelector('thead tr.mobile-header-row');
        const secondThs = table.querySelectorAll('thead tr.mobile-header-row th');
        if (!firstRow || !secondRow || !secondThs || secondThs.length === 0) return;

        const tableContainer = this.$el.querySelector('.table-container');

        // Robustly measure positions: retry a few times if zero (rendering may be async)
        let firstRect = firstRow.getBoundingClientRect();
        let secondRect = secondRow.getBoundingClientRect();
        let attempts = 0;
        while ((firstRect.height === 0 || secondRect.height === 0) && attempts < 6) {
          attempts++;
          const start = performance.now();
          while (performance.now() - start < 25) {}
          firstRect = firstRow.getBoundingClientRect();
          secondRect = secondRow.getBoundingClientRect();
        }

        // Ensure the mobile second header row is visible on mobile (override CSS hiding rules)
        try {
          if (secondRow && window.innerWidth <= 576) {
            secondRow.style.setProperty('display', 'table-row', 'important');
            secondRow.setAttribute('aria-hidden', 'false');
          }
        } catch (e) {}

        // Compute top for sticky relative to the scrolling container (if any)
        const containerRect = tableContainer ? tableContainer.getBoundingClientRect() : null;
        // topForSticky is the distance (in px) from the top of the scroll container to the bottom of the first row
        const topForSticky = Math.max(0, Math.round(firstRect.bottom - (containerRect ? containerRect.top : 0)));

        // Apply sticky to the TH(s) inside the mobile header row so it stays under the first row
        secondThs.forEach(th => {
          try {
            th.style.setProperty('position', 'sticky', 'important');
            th.style.setProperty('top', topForSticky + 'px', 'important');
            th.style.zIndex = '9998';
            // enforce corporate red and white text to match top header
            th.style.background = '#99172A';
            th.style.color = '#fff';
            th.style.display = 'table-cell';
            th.style.visibility = 'visible';
          } catch (e) {}
        });

        // Previously we added padding-top to .table-container to avoid header overlap on mobile.
        // That behavior caused layout shifts; remove any dynamic padding-top and let headers be sticky
        // or handled via fixed elements elsewhere.
        try {
          const tableContainer = this.$el.querySelector('.table-container');
          if (tableContainer) {
            // Ensure no padding-top is present
            tableContainer.style.removeProperty('padding-top');
          }
        } catch (e) {}

        // Fallback: if on mobile and sticky support seems flaky, show a fixed small header
        try {
          const mfh = this.$el.querySelector('.mobile-fixed-second-header');
          if (mfh) {
            if (window.innerWidth <= 576) {
              // Use the viewport coordinates of the first row to put the fixed header right below it
              // Preferir la referencia visual principal '.table-header' si está presente
              const tableHeaderEl = document.querySelector ? document.querySelector('.table-header') : null;
              const headerRect = tableHeaderEl ? tableHeaderEl.getBoundingClientRect() : firstRow.getBoundingClientRect();
              const topForFixed = Math.max(0, Math.round(headerRect.bottom));
              // Apply styles with important priority so CSS rules with !important don't override the computed values
              try {
                mfh.style.setProperty('display', 'flex', 'important');
                mfh.style.setProperty('position', 'fixed', 'important');
                mfh.style.setProperty('top', topForFixed + 'px', 'important');
                mfh.style.setProperty('z-index', '9998', 'important');
                mfh.style.setProperty('background', '#99172A', 'important');
                mfh.style.setProperty('color', '#fff', 'important');
              } catch (e) {}

              // Align horizontally with the table container to match column content
              try {
                if (tableContainer) {
                  const tableRect = tableContainer.getBoundingClientRect();
                  mfh.style.setProperty('left', Math.round(tableRect.left) + 'px', 'important');
                  mfh.style.setProperty('width', Math.round(tableRect.width) + 'px', 'important');
                  mfh.style.setProperty('right', 'auto', 'important');
                } else {
                  mfh.style.setProperty('left', '0', 'important');
                  mfh.style.setProperty('right', '0', 'important');
                  mfh.style.setProperty('width', 'auto', 'important');
                }
              } catch (e) {}
            } else {
              mfh.style.display = 'none';
            }
          }
        } catch (e) {}
      } catch (e) {
        // noop
      }
    },
    applyRowStriping() {
      try {
        if (!this.$el) return;
        const tbody = this.$el.querySelector('.user-table tbody');
        if (!tbody) return;
        // Agrupar filas por ticket robustamente: cada ticket tiene al menos una fila
        // principal; si la siguiente fila tiene la clase 'mobile-extra-row' se considera
        // parte del mismo ticket. Construimos grupos dinámicos y alternamos por grupo.
        const allRows = Array.from(tbody.querySelectorAll('tr'));
        const groups = [];
        for (let i = 0; i < allRows.length; i++) {
          const row = allRows[i];
          if (!row) continue;
          // Si la fila es una 'mobile-extra-row', agrégala al último grupo (si existe)
          if (row.classList.contains('mobile-extra-row')) {
            if (groups.length === 0) {
              // fila extra sin principal: crea grupo nuevo con solo esta fila
              groups.push([row]);
            } else {
              groups[groups.length - 1].push(row);
            }
          } else {
            // fila principal: iniciar nuevo grupo y, si la siguiente fila es mobile-extra, se añadirá en la siguiente iteración
            groups.push([row]);
          }
        }

        // Aplicar alternancia por grupo: 0 -> white, 1 -> alt, 2 -> white, ...
        groups.forEach((grp, gi) => {
          const shouldAlt = (gi % 2) === 1;
          grp.forEach(tr => {
            if (shouldAlt) tr.classList.add('row-alt'); else tr.classList.remove('row-alt');
          });
        });
        // debug: exponer información mínima en consola para diagnóstico
        try {
          // build a compact summary per ticket (first 20 rows -> 10 tickets)
          const summary = [];
          for (let i = 0; i < Math.min(allRows.length, 40); i += 2) {
            const a = allRows[i];
            const b = allRows[i + 1];
            const styleA = a && window.getComputedStyle ? getComputedStyle(a) : null;
            const styleB = b && window.getComputedStyle ? getComputedStyle(b) : null;
            summary.push({ ticketIndex: i / 2, r1: a ? a.className : '', bg1: styleA ? styleA.backgroundColor : 'n/a', r2: b ? b.className : '', bg2: styleB ? styleB.backgroundColor : 'n/a' });
          }
          // debug summary removed to reduce console noise
        } catch (e) {}

        // fallback: si el render es lento, reintentar una vez con un pequeño delay
        try {
          setTimeout(() => { try { this.applyRowStriping(); } catch (e) {} }, 80);
        } catch (e) {}

  // debug helper removed to avoid console noise
      } catch (e) {
        // noop
      }
    },
    clearSearch() {
      try {
        this.searchQuery = '';
        this.filterTickets();
        // focus the input after clearing
        this.$nextTick(() => {
          const el = this.$el && this.$el.querySelector ? this.$el.querySelector('#busquedaUnificada') : null;
          if (el) el.focus();
        });
      } catch (e) {}
    },
    
      // --- Inactivity handlers (delegados al inactivityWatcher) ---
      handleKeepAliveFromModal() {
        try {
          if (this.inactivityWatcher && this.inactivityWatcher.keepAlive) this.inactivityWatcher.keepAlive();
          // force a load to refresh token on server
          try { this.loadTickets(); } catch (e) {}
            // El usuario ha forzado keep-alive / renovación de token: manejar según el gestor global
            try {
              if (this.autoRefreshManager) {
                if (this.autoRefreshManager.finalScheduled) {
                  if (this.debugLayout) console.info('[WelcomeView] keep-alive desde modal: hay último refresco pendiente; no reinicio contador ni limpio timeout');
                } else {
                  try { this.autoRefreshManager.refreshCount = 0; } catch (e) { this.autoRefreshManager.refreshCount = 0; }
                  try { if (this.autoRefreshManager.lastTimeoutId) { clearTimeout(this.autoRefreshManager.lastTimeoutId); this.autoRefreshManager.lastTimeoutId = null; } } catch (e) {}
                  if (this.debugLayout) console.info('[WelcomeView] refreshCount reiniciado por keep-alive desde modal; timeout final limpio');
                  if (this.userRole !== 4) this.ensureAutoRefreshRunning();
                }
              } else {
                // fallback al comportamiento local
                try { this.refreshCount = 0; } catch (e) {}
                try { if (this.lastRefreshTimeoutId) { clearTimeout(this.lastRefreshTimeoutId); this.lastRefreshTimeoutId = null; } } catch (e) {}
                if (this.debugLayout) console.info('[WelcomeView] refreshCount reiniciado por keep-alive desde modal; timeout final limpio (fallback)');
                if (this.userRole !== 4) this.ensureAutoRefreshRunning();
              }
            } catch (e) {}
        } catch (e) {}
      },

      ensureAutoRefreshRunning() {
          try {
            if (this.userRole === 4) return;
            // Use the global manager: do not start if we've reached the limit or a final is scheduled
            if (Number(this.autoRefreshManager.refreshCount || 0) >= Number(this.maxAutoRefresh || 2) || this.autoRefreshManager.finalScheduled) {
              console.info(`[WelcomeView] ensureAutoRefreshRunning: límite de refrescos alcanzado (${this.autoRefreshManager.refreshCount}/${this.maxAutoRefresh}) o final programado, no se reiniciará el auto-refresh`);
              return;
            }

            // Ensure only one global interval exists at a time
            if (this.autoRefreshManager.timerId) {
              try { clearInterval(this.autoRefreshManager.timerId); } catch (e) {}
              this.autoRefreshManager.timerId = null;
            }

            // Clear any pending final-timeout if we're restarting and it's not protected
            if (this.autoRefreshManager.lastTimeoutId && !this.autoRefreshManager.finalScheduled && !this.autoRefreshManager.lastProtected) {
              try { clearTimeout(this.autoRefreshManager.lastTimeoutId); } catch (e) {}
              this.autoRefreshManager.lastTimeoutId = null;
            }

            // Create global interval
            this.autoRefreshManager.instanceCounter = (Number(this.autoRefreshManager.instanceCounter) || 0) + 1;
            const managerInstance = this.autoRefreshManager.instanceCounter;
            this.autoRefreshManager.timerId = setInterval(async () => {
              try {
                // If this interval is stale (a previous instance), ignore its callback
                if (managerInstance !== this.autoRefreshManager.instanceCounter) {
                  if (this.debugLayout) console.info(`[WelcomeView] Ignorando intervalo antiguo del gestor (instance ${managerInstance} != current ${this.autoRefreshManager.instanceCounter})`);
                  return;
                }
                // If we've reached the limit right before executing, stop
                if (Number(this.autoRefreshManager.refreshCount || 0) >= Number(this.maxAutoRefresh || 2)) {
                  try { clearInterval(this.autoRefreshManager.timerId); } catch (e) {}
                  this.autoRefreshManager.timerId = null;
                  console.info(`[WelcomeView] Auto-refresh detenido en gestor: límite ${this.maxAutoRefresh} alcanzado (instance ${managerInstance})`);
                  try { this.showToast && this.showToast(this.$t?.('auto_refresh_limit_reached') || `Auto-refresh detenido tras ${this.maxAutoRefresh} refrescos`, 'info'); } catch (e) {}
                  return;
                }

                // Ejecutar la carga (isAuto=true) desde el gestor
                await this.loadTickets(true, { fromManager: true });
              } catch (e) {
                console.error('Error en auto-refresh (gestor):', e);
              }
            }, this.autoRefreshIntervalMs);
          } catch (e) {}
      },

  },
};
</script>

<style scoped>
.filters-row-align {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px; /* aún más compacto */
}
.col-incidencia,
.col-creado,
.col-asignado {
  max-width: 250px;
  white-space: pre-line;
  word-break: break-word;
  overflow-wrap: break-word;
  text-overflow: ellipsis;
  vertical-align: top;
}
.col-creado,
.col-asignado {
  max-width: 210px;
  white-space: pre-line;
  word-break: break-word;
  overflow-wrap: break-word;
  text-overflow: ellipsis;
  vertical-align: top;
}

.welcome-container {
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
  /* reduced padding-bottom so table visible area is closer to footer */
  padding: 0 20px 40px 20px; /* padding-bottom to keep footer separated */
  min-height: 100vh; /* ensure the container fills the viewport so sticky headers work reliably */
  height: 100vh; /* lock to viewport height so body doesn't scroll */
  overflow: hidden; /* prevent body scrolling; allow .table-container to scroll */
  box-sizing: border-box;
}

.table-container {
  width: 100%;
  /* make the table container the flexible, scrollable main area */
  flex: 1 1 auto; /* allow it to grow and be the largest area */
  min-height: 0; /* allow the flex child to scroll properly */
  -webkit-overflow-scrolling: touch;
  overflow-y: auto;
  margin-top: 0;
  margin-bottom: 24px; /* reduced space before footer */
}

.title-container {
  margin-top: 16px; /* espacio adicional sobre el contenedor del título */
  margin-bottom: 6px; /* espacio reducido entre título y filtros */
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px; /* un poco más de espacio entre título y botón */
}

.title-left {
  display: flex;
  align-items: center;
  gap: 8px; /* espacio pequeño entre título y botón */
  white-space: nowrap; /* evitar saltos de línea entre título y botón */
}

.title-left .title { margin: 0; }
.title-left .btn-add-ticket { margin-left: 4px; }

.title-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px; /* reducir separación entre elementos de acciones */
  min-width: 0; /* permitir que los hijos colapsen en pantallas pequeñas */
  /* Minimal mobile-only rules to ensure the search (lupa) button sits visually inside
     the search input without altering the rest of the layout. These are scoped to
     .title-actions so they don't affect other potential search rows. */
  .title-actions .search-input-wrapper {
    position: relative;
    width: 100%;
    max-width: none;
  }

  .title-actions .search-input-wrapper .filter-input {
    /* Reserve space for the clear icon + search icon inside the input */
    padding-right: 72px !important;
    box-sizing: border-box;
  }

  .title-actions .search-input-wrapper .search-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    z-index: 5;
    pointer-events: auto;
  }

  .title-actions .search-input-wrapper .clear-icon {
    position: absolute;
    right: 48px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 5;
  }
}

.title-filter-estado {
  display: flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 576px) {
  /* Mostrar en móvil la primera fila con solo Nº/Fecha/Incidencia y la segunda con Creado/Ubicación/Categoría */
  .user-table thead tr.first-row th.hide-on-first { display: none !important; visibility: hidden !important; }
  .user-table thead tr.second-row th.hide-on-second { display: none !important; visibility: hidden !important; }

  /* Asegurar que las dos filas del thead sean visibles y sticky en móvil */
  .user-table thead tr.first-row th,
  .user-table thead tr.second-row th {
    display: table-cell !important;
    position: sticky !important;
    /* Make header sticky but avoid forcing a high stacking order so modals can appear above it. */
    z-index: auto !important;
    background-color: #99172A !important;
    color: #fff !important;
  }

  /* Ajuste: la segunda fila quedará justo debajo de la primera; computeSecondHeaderTop() gestiona el top */
  .user-table thead tr.second-row th { will-change: top; background-color: #99172A !important; }
  /* Asegurar que la fila de encabezado móvil también use el rojo corporativo */
  .user-table thead tr.mobile-header-row th { background-color: #99172A !important; }
  /* Fallback fijo (mobile-fixed-second-header) debe coincidir visualmente con el segundo header */
  .mobile-fixed-second-header {
    background-color: #99172A !important;
    color: #fff !important;
    display: flex;
    gap: 8px;
    padding: 6px 8px;
    align-items: center;
    justify-content: flex-start;
    /* Avoid outranking overlays — let modals/backdrops control stacking. */
    z-index: auto !important;
  }
  /* Ensure the mobile second header row and its fallback fixed header have no rounded corners */
  .user-table thead tr.second-row th,
  .user-table thead tr.mobile-header-row th,
  .mobile-fixed-second-header {
    border-radius: 0 !important;
  }
  /* Also explicitly remove radius on first/last cells of the second header row to avoid inherited rounding */
  .user-table thead tr.second-row th:first-child,
  .user-table thead tr.second-row th:last-child,
  .user-table thead tr.mobile-header-row th:first-child,
  .user-table thead tr.mobile-header-row th:last-child {
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }
  .mobile-fixed-second-header .mfh-item {
    color: #fff !important;
    font-weight: 600;
  }
  /* Mobile: show a small dot to the left of the ticket number that matches the priority color */
  .user-table tbody tr td.col-numero {
    position: relative;
    /* ensure space for the dot */
    padding-left: 34px !important;
  }

  .user-table tbody tr td.col-numero::before {
    content: '';
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: 10px;
    border-radius: 50%;
    /* outer black border + subtle inner inset shadow to match style */
    box-shadow: 0 0 0 1px #000, 0 0 0 1px rgba(0,0,0,0.03) inset;
    /* default neutral color in case no priority class matches */
    background: #798a99;
  }

  /* color the dot to match the pill backgrounds defined above */
  .user-table tbody tr.priority-critical td.col-numero::before,
  .user-table tbody tr.priority-high td.col-numero::before {
    background: var(--prio-critical) !important; /* red */
  }
  .user-table tbody tr.priority-medium td.col-numero::before {
    background: var(--prio-medium) !important; /* orange */
  }
  .user-table tbody tr.priority-low td.col-numero::before {
    background: var(--prio-low) !important; /* green */
  }
  /* En móvil: apilar el título y las acciones para que la búsqueda y el botón de filtros
     queden debajo de 'Tickets' y 'Nuevo ticket' */
  .title-container {
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 8px;
  }

  /* Primera fila: título y botón 'Nuevo ticket' alineados en una sola línea */
  .title-left {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    width: 100% !important;
    gap: 8px;
    white-space: normal !important;
  }

  /* Segunda fila: acciones (búsqueda + filtros) ocupan toda la anchura */
  .title-actions {
    width: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 8px;
    margin-top: 4px;
    box-sizing: border-box;
  }

  /* Orden explícito para que el botón 'Añadir' quede a la izquierda y las acciones a la derecha */
  .title-left .btn-add-ticket { order: 1; margin-left: 8px; }
  .title-actions .search-input-wrapper { order: 1; width: 100%; max-width: none; min-width: 120px; }
  .title-actions .filter-actions { order: 2; display: flex; align-items: center; }

  /* Asegurar que el botón de filtros no ocupe toda la línea y tenga buen contraste */
  .filters-toggle-btn { padding: 6px 10px; height: 38px; display: inline-flex; align-items: center; border-radius: 6px; }
  .filters-toggle-btn .btn-text { margin-right: 6px; }

  /* Asegurar que la búsqueda en header se muestre correctamente y no sea oculta por overflow */
  .title-actions .search-input-wrapper { display: flex !important; align-items: center; min-width: 120px; max-width: 70%; }
  .title-actions .search-input-wrapper .filter-input { width: 100% !important; max-width: none !important; }
}

/* Desktop-only duplicate header row: visible on desktop, hidden on small screens */
.desktop-header-row {
  display: table-row;
}

/* By default hide the mobile header row; JS/CSS will show it on mobile */
.mobile-header-row {
  display: none;
}

@media (max-width: 576px) {
  /* On mobile show BOTH header rows: keep the desktop header as the first row
     and show the mobile header as the second row so computeSecondHeaderTop()
     can position the second row under the first. */
  .desktop-header-row {
    display: table-row !important;
    visibility: visible !important;
  }
  .mobile-header-row {
    display: table-row !important;
    visibility: visible !important;
  }
}

/* Fix: remove thin white/grey lines between header cells and between header rows on mobile */
@media (max-width: 576px) {
  .user-table {
    border-collapse: collapse; /* ensure collapse */
    border-spacing: 0; /* remove any cell spacing */
  }
  .user-table thead th {
    border: none !important; /* remove borders on header cells */
    background-color: #99172A !important; /* ensure full red background */
    background-clip: padding-box; /* avoid hairline gaps from borders */
    padding-left: 8px !important; /* normalize padding for mobile */
    padding-right: 8px !important;
  }
  /* Remove any gap between the two header rows */
  .user-table thead tr {
    border: none !important;
    margin: 0 !important;
  }
  .user-table thead tr + tr th {
    border-top: none !important;
  }
}

/* Mobile: eliminar líneas verticales/hairlines entre celdas de la cabecera */
@media (max-width: 576px) {
  /* Forzar colapso y eliminar espaciado que puede producir seams */
  .user-table,
  .user-table thead,
  .user-table tbody,
  .user-table tr,
  .user-table th,
  .user-table td {
    border-collapse: collapse !important;
    border-spacing: 0 !important;
    background-clip: padding-box !important;
    -webkit-background-clip: padding-box !important;
  }

  /* Asegurar que los TH no dibujen bordes ni sombras que generen líneas verticales */
  .user-table thead th {
    border: none !important;
    border-left: none !important;
    border-right: none !important;
    box-shadow: none !important;
    background-color: #99172A !important; /* reaplicar color para cubrir seams */
    background-clip: padding-box !important;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    transform: translateZ(0);
  }

  /* Asegurar que entre TH adyacentes no quede línea (algunas engines dibujan 1px en el límite) */
  .user-table thead th + th {
    border-left: none !important;
    box-shadow: none !important;
  }

  /* Si el seam viene por rounding subpixel, reducir la posibilidad usando line-height/height coherente */
  .user-table thead tr {
    line-height: 1;
  }
}

.add-user-container {
  margin-bottom: 20px;
}

.user-table {
  width: 100%;
  /* Usar border-collapse collapse para evitar gaps entre filas; la línea
     fina se dibuja con ::after en cada tr */
  border-collapse: collapse;
}

.user-table th,
.user-table td {
  border: none;
  padding: 8px;
  text-align: left;
  background: transparent; /* celdas transparentes para respetar el contorno del TR */
}

  .user-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #99172A; /* corporate red */
  color: #fff; /* white text for headers */
  text-align: left;
  padding: 8px;
  border-bottom: 1px solid #e0e0e0; /* borde inferior más fino */
  }

.user-table tbody tr {
  /* Filas de datos: fondo blanco por defecto. Bordes más finos y color suave */
  background-color: #ffffff !important;
  border: 0.4px solid rgba(0,0,0,0.12) !important; /* línea más fina y menos contrastada */
  box-shadow: none !important;
}

/* Para que dos filas consecutivas (main + mobile-extra-row) parezcan un solo contenedor:
   - la fila principal no dibuja la línea inferior;
   - la fila extra no dibuja la línea superior;
   - se aplican radios en la parte superior e inferior respectivamente. */
.user-table tbody tr:not(.mobile-extra-row) {
  border-bottom: none !important;
  border-radius: 6px 6px 0 0 !important;
}
.user-table tbody tr.mobile-extra-row {
  border-top: none !important;
  border-radius: 0 0 6px 6px !important;
}

/* Alternancia clara: una fila blanca, la siguiente #F0F0F0 */
.user-table tbody tr:not(.mobile-extra-row):nth-child(even) {
  background-color: #F0F0F0 !important;
}

/* Clase utilizada por JS para alternar filas ignorando filas auxiliares */
.user-table tbody tr.row-alt {
  background-color: #F0F0F0 !important;
}

/* Columna 'nivel' coloreada según prioridad (se aplica a la celda .col-nivel del tr con clase priority-*) */
/* Ensure the TD keeps the row background (transparent) so its padding shows the row color.
   The colored 'pill' inside will hold the priority background. */
.user-table td.col-nivel {
  background: transparent !important;
  padding: 8px; /* uniform padding set earlier */
  vertical-align: middle;
}

/* Small pill that receives the priority background; keeps visible gap to the TD edges */
.nivel-pill {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 700;
  color: #000 !important;
  box-sizing: border-box;
}

/* Priority backgrounds applied to the pill */
.welcome-container {
  /* CSS variables to ensure priority colors are the same for desktop pills and mobile dots */
  --prio-critical: #f8d7da;
  --prio-high:    #f8d7da;
  --prio-medium:  #fff3cd;
  --prio-low:     #d1e7dd;
}

.user-table tbody tr.priority-critical td .nivel-pill,
.user-table tbody tr.priority-high td .nivel-pill {
  background-color: var(--prio-high) !important; /* red */
}
.user-table tbody tr.priority-medium td .nivel-pill {
  background-color: var(--prio-medium) !important; /* orange */
}
.user-table tbody tr.priority-low td .nivel-pill {
  background-color: var(--prio-low) !important; /* green */
}

.action-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #6c757d; /* gris neutro */
  color: #ffffff !important; /* texto blanco, forzar por si hay reglas específicas */
  width: 30px;
  height: 30px;
  border-radius: 4px;
  font-size: 18px !important;
  line-height: 1;
  margin: 0 6px;
  cursor: pointer;
}

.action-icon:hover {
  background: #5a6268; /* ligeramente más oscuro al pasar */
  color: #fff;
  transform: scale(1.03);
}

.logout-button {
  background-color: #99172A;
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
}

.logout-button:hover {
  background-color: #7A1222;
}

.logs-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  table-layout: fixed;
}

.login-fallido {
  background-color: #ffe6e6;
  color: #99172A;
}

.expulsado-x {
  color: red;
  font-weight: bold;
}

.expulsado-check {
  color: green;
  font-weight: bold;
}

.fallo {
  color: orange;
  font-weight: bold;
}

.modal-fixed {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  background-color: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}

.sticky-header {
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 3;
  padding: 10px 0;
  border-bottom: 1px solid #ddd;
}

.add-ticket-icon {
  font-size: 36px;
  color: #99172A;
  cursor: pointer;
  transition: color 0.3s ease;
}

.add-ticket-icon:hover {
  color: #7A1222;
}

.badge.bg-danger {
  color: white;
  background-color: red;
  font-size: 14px;
  font-weight: bold;
  padding: 5px 10px;
  border-radius: 5px;
}

.bloqueado-column {
  text-align: center;
  vertical-align: middle;
}

.bloqueado-icon {
  color: #dc3545 !important;
  font-size: 24px !important;
  vertical-align: middle;
  transition: color 0.2s;
}

.bloqueado-icon:hover {
  color: #c82333 !important;
  transform: scale(1.1);
}

.modal-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: none;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.red-header {
  background-color: #99172A;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.red-header .modal-title {
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  line-height: 1.4;
}

.red-header .user-name {
  font-size: 16px;
  font-weight: normal;
}

.red-header .btn-close {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: white;
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.btn-primary {
  background-color: #99172A;
  border-color: #99172A;
  color: white;
}

.btn-primary:hover {
  background-color: #7A1222;
  border-color: #7A1222;
}

.btn-add-ticket {
  background-color: #2e7d32 !important;
  color: #fff !important;
  border: none;
  border-radius: 5px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin-bottom: 0;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-add-ticket:hover {
  background-color: #1b5e20 !important;
  color: #fff !important;
}

.filter-container {
  display: flex;
  align-items: center;
}

/* Fila superior dentro de la zona de filtros: contiene búsqueda + estado (desktop) + acciones */
.filter-row-top {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.filter-row-top .search-input-wrapper {
  /* ocupar espacio disponible a la izquierda para que selects queden a la derecha */
  flex: 1 1 420px;
  min-width: 220px;
}

/* Desktop: keep label + control on the same line */
.filter-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-right: 12px;
}

.filter-label {
  margin-right: 6px;
  font-weight: bold;
  color: #333;
  width: auto; /* permitir ancho natural en pantallas >576px */
  flex-shrink: 1;
  font-size: 14px;
  line-height: 1.2;
}

.filter-select {
  padding: 6px 12px;
  font-size: 14px;
  color: #000;
  background-color: #fff;
  border: 1px solid #d0d0d0;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s, border-color 0.2s;
  margin-right: 18px;
  outline: none;
  font-weight: bold;
  height: 38px;
  /* Estilo de apariencia y flecha negra */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24'><path fill='black' d='M7 10l5 5 5-5z'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 2.2em 2.2em; /* flecha más grande */
  padding-right: 44px; /* espacio para la flecha */
}

.filter-select:hover,
.filter-select:focus {
  background-color: #f6f6f6;
  color: #000;
  border-color: #cfcfcf;
}

#busquedaUnificada {
  /* Campo de búsqueda más amplio por defecto (desktop) */
  width: 320px !important;
  min-width: 200px;
  max-width: 560px;
  background-color: #fff !important;
  color: #333 !important;
  text-align: left;
  padding: 8px 12px !important;
  padding-right: 44px !important; /* espacio para el icono */
  box-sizing: border-box !important;
  border-radius: 5px;
  /* No usar background-image: usamos el icono de Material Icons posicionado dentro del wrapper */
}

/* estilo claro para el input de búsqueda por descripción */
.filter-input {
  min-width: 220px;
  max-width: 420px;
  padding: 6px 10px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: #fff;
  color: #333;
  margin-right: 18px;
  height: 38px;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 220px;
  max-width: 420px;
}


.search-input-wrapper .filter-input {
  /* Reservar espacio para dos iconos dentro del input. */
  padding-right: 96px !important; /* aumentar espacio para que los iconos no tapen el borde */
  width: 100%;
  box-sizing: border-box;
}

/* Iconos anclados dentro del input: circulitos visualmente dentro del campo */
.search-input-wrapper .search-icon,
.search-input-wrapper .clear-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  cursor: pointer;
  user-select: none;
  z-index: 3; /* encima del input */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: transparent; /* transparente para no tapar el borde del input */
}

/* Make icon buttons accessible: reset default button styles and provide focus ring */
.search-input-wrapper button.search-icon,
.search-input-wrapper button.clear-icon {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  line-height: 1;
}

.search-input-wrapper button.search-icon:focus,
.search-input-wrapper button.clear-icon:focus {
  outline: none;
}

.search-input-wrapper button.search-icon:focus-visible,
.search-input-wrapper button.clear-icon:focus-visible {
  box-shadow: 0 0 0 3px rgba(100,150,250,0.25);
 
  border-radius: 6px;
}

.search-input-wrapper .search-icon {
  right: 20px; /* más dentro del input */
  color: #798a99;
}

.search-input-wrapper .clear-icon {
  right: 56px; /* a la izquierda de la lupa, también dentro */
  color: #999;
}

/* Hover: indicar área clicable sin tapar el borde */
.search-input-wrapper .search-icon:hover,
.search-input-wrapper .clear-icon:hover {
  background: rgba(0,0,0,0.04);
}

@media (max-width: 576px) {
  .search-input-wrapper .search-icon { right: 12px; }
  .search-input-wrapper .clear-icon { right: 40px; }
  .search-input-wrapper .filter-input { padding-right: 72px !important; }
}

@media (min-width: 769px) {
  .search-input-wrapper { width: 320px; }
}
@media (max-width: 768px) {
  .filter-input { width: 100%; max-width: none; }
}

.password-container {
  position: relative;
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  cursor: pointer;
  color: #99172A;
}

.password-toggle:hover {
  color: #7A1222;
}

.form-check-input {
  accent-color: #99172A;
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.form-check-label {
  color: #000;
  font-weight: bold;
  margin-left: 5px;
}

.icon-row-horizontal {
  display: flex;
  gap: 15px;
  justify-content: center;
  align-items: center;
  padding: 5px;
}

.icon-row-horizontal .material-icons {
  color: #99172A !important;
  font-size: 24px !important;
  vertical-align: middle;
  transition: color 0.2s, transform 0.2s;
  cursor: pointer;
  user-select: none;
}

.icon-row-horizontal .material-icons:hover {
  color: #7A1222 !important;
  transform: scale(1.1);
}

.lock-icon {
  color: #dc3545 !important;
}

.lock-icon:hover {
  color: #c82333 !important;
  transform: scale(1.1);
}

.unlock-icon {
  color: #28a745 !important;
}

.unlock-icon:hover {
  color: #218838 !important;
  transform: scale(1.1);
}

.actions {
  text-align: center;
  white-space: nowrap;
}

/* Colores de prioridad: no aplicar background a la fila completa.
  Las reglas de color aplicarán al elemento interno .nivel-pill para
  preservar el padding de la celda. */

/* Mantener el coloreado sólo en la celda .col-nivel para priorizar la legibilidad
   y evitar que la fila completa recobre color. No forzar el fondo transparente
   en la fila (eso estaba anulando la alternancia). */


/* Asegurar que la alternancia se aplique a las celdas de la fila: si se deja
   el background en el TR puede ser sobrescrito por otras reglas; aplicarlo
   a los TD con alta especificidad y !important para ganar la cascada. */
.user-table tbody tr.row-alt td {
  background-color: #F0F0F0 !important;
}

/* Cuando la fila está alternada, forzar que la celda .col-nivel mantenga
   su color de prioridad (rojo / naranja / verde) igual que en las filas blancas. */
/* Cuando la fila está alternada, aplicar el color de prioridad únicamente
   al pill interno y dejar el TD con el fondo de la fila. */
.user-table tbody tr.row-alt.priority-high td .nivel-pill {
  background-color: #f8d7da !important; /* rojo claro (high) */
}
.user-table tbody tr.row-alt.priority-medium td .nivel-pill {
  background-color: #fff3cd !important; /* naranja claro (medium) */
}
.user-table tbody tr.row-alt.priority-low td .nivel-pill {
  background-color: #d1e7dd !important; /* verde claro (low) */
}

.btn-ticket-margin {
  margin-right: 18px;
  margin-bottom: 0;
  align-self: flex-end;
}

.swal2-container {
  z-index: 130000 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  background: rgba(0, 0, 0, 0.5) !important;
}

.swal2-popup {
  box-sizing: border-box !important;
  display: grid !important;
  grid-template-columns: auto 1fr !important;
  grid-template-rows: auto 1fr auto !important;
  gap: 12px 18px !important;
  align-items: center !important;
  width: auto !important;
  max-width: 600px !important;
  padding: 18px 20px !important;
  background: #ffffff !important;
  border-radius: 8px !important;
  box-shadow: 0 8px 30px rgba(0,0,0,0.18) !important;
  margin: 0 !important;
  transform: none !important;
  overflow: visible !important;
}

.swal2-popup .swal2-icon {
  width: 64px !important;
  height: 64px !important;
  margin: 0 !important;
  justify-self: center !important;
  align-self: start !important;
}

.modal-overlay,
.modal-container {
  z-index: 115000 !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
}

.swal2-container.swal2-top-end {
  align-items: flex-start !important;
  justify-content: flex-end !important;
  padding: 0.5rem !important;
}


.modal-overlay,
.modal-container {
  display: none;
  justify-content: center;

  /* Make selects/inputs full width on small screens */
  .filter-select,
  .filter-input {
    width: 100% !important;
    max-width: none !important;
    margin-right: 0 !important;
  }
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 99999;
  visibility: hidden;
  opacity: 0;
  transition: opacity 180ms ease;
}
.modal-overlay.open,
.modal-container.open {
  display: flex;
  visibility: visible;
  opacity: 1;
}

.modal-overlay, .modal-container {
  display: flex !important;
  justify-content: center;
  align-items: center;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
  z-index: 99999 !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.swal2-center {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}
.swal2-center .swal2-popup {
  margin: 0 !important;
  transform: none !important;
}

.table-container {
  -webkit-overflow-scrolling: touch;
  overflow-y: auto;
}

/* Por defecto ocultar filas pensadas solo para móvil */
.mobile-extra-row,
.mobile-header-row {
  display: none;
}

/* Alineación de filtros en escritorio: colocarlos debajo de la cabecera y alineados a la derecha */
.filters-row {
  display: flex;
  width: 100%;
  margin-top: 4px; /* espacio reducido entre las dos filas de filtros */
  margin-bottom: 12px; /* espacio añadido entre filtros y la tabla */
  box-sizing: border-box;
}

.filters-row .filter-group {
  margin-left: 12px;
}

/* Empujar los filtros a la derecha en pantallas grandes */
@media (min-width: 769px) {
  .filters-row {
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
  }
  /* Hacer que el select de estado se alinee con el borde derecho del search input */
  .title-actions { display: flex; align-items: center; }
  .title-actions .search-input-wrapper { margin-left: 12px; }
  .filter-estado-right { min-width: 180px; text-align: right; }
}

@media (max-width: 768px) {
  .filters-row-align {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .btn-add-ticket {
    padding: 6px 10px;
    font-size: 13px;
    margin-right: 0;
    align-self: center;
  }

  /* Alineación del botón de alternar filtros junto a Nuevo ticket */
  .filters-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .filter-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .filters-toggle-btn {
    /* Igualar altura y aspecto al botón principal */
    padding: 8px 14px;
    font-size: 13px;
    height: 38px;
    line-height: 1;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  background-color: #6c757d;
  color: #fff;
    border: none;
    border-radius: 5px;
    align-self: center;
    font-weight: bold;
    gap: 8px;
    padding-right: 12px;
  }

  .filters-toggle-btn .material-icons {
    font-size: 20px;
    vertical-align: middle;
    line-height: 1;
  }

  .filters-toggle-btn .btn-text {
    font-weight: 700;
    margin-right: 4px;
  }

  .filter-select,
  #busquedaUnificada {
    height: 34px;
    padding: 8px;
    font-size: 13px;
    box-sizing: border-box;
  }

  #busquedaUnificada {
    /* en pantallas <=768 usar tamaño compacto pero legible */
    width: 100% !important;
    max-width: none;
    padding: 8px !important;
  }

  .user-table th,
  .user-table td {
    padding: 8px;
    font-size: 13px;
    line-height: 1.2;
  }

  .user-table thead th {
    position: static;
    top: auto;
    z-index: 1;
  }

  .table-container {
    /* use flex behavior on small screens as well */
    flex: 1 1 auto;
    min-height: 0;
  }
}

@media (max-width: 420px) {
  .user-table th,
  .user-table td {
    padding: 8px;
    font-size: 12px;
  }

  .table-container {
    flex: 1 1 auto;
    min-height: 0;
  }
}

/* Ocultar columnas menos relevantes en móviles (vertical) hasta 576px (bootstrap sm) */
@media (max-width: 576px) {
  /* Ocultamos columnas menos relevantes en móvil (excepto las que manejamos en la fila extra) */
  .user-table th.col-ubicacion,
  .user-table td.col-ubicacion,
  .user-table th.col-categoria,
  .user-table td.col-categoria,
  .user-table th.col-nivel,
  .user-table td.col-nivel,
  .user-table th.col-acciones,
  .user-table td.col-acciones {
    display: none !important;
  }

  /* Asegurar que la columna de incidencia ocupe espacio y sea legible */
  .user-table th.col-incidencia,
  .user-table td.col-incidencia {
    text-align: left;
  }

  /* Ajustes de padding/fuente para mejorar lectura en móvil */
  .user-table th,
  .user-table td {
    padding: 6px 8px;
    font-size: 13px;
  }

  .table-container {
    flex: 1 1 auto;
    min-height: 0;
  }

  /* reducir el padding-bottom para móviles */
  .welcome-container { padding-bottom: 56px; }
  /* Make labels fluid on small screens */
  .filter-label {
    width: auto !important;
    margin-right: 0 !important;
    flex-shrink: 1 !important;
  }
  /* Equalize select widths on mobile */
  .filter-select {
    width: 100% !important;
    max-width: 360px !important;
  }
  /* Make labels same width as selects on mobile */
  .filter-label {
    width: 100% !important;
    max-width: 360px !important;
    text-align: left;
    display: block;
    margin-bottom: 6px;
  }

  /* Ajustes específicos para la fila de búsqueda en móvil: centrar labels e inputs y espacio con los botones */
  .search-row {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    width: 100%;
    box-sizing: border-box;
  }

  .search-row .filter-label {
    width: 100% !important;
    max-width: 360px !important;
    text-align: center !important;
    margin-bottom: 4px !important;
    font-size: 14px !important;
    line-height: 1.2 !important;
  }

  .search-row .filter-input,
  .search-row .filter-select {
    width: 100% !important;
    max-width: none !important; /* allow full width on mobile */
    margin: 0 !important;
    box-sizing: border-box;
  }

  /* Anclar iconos dentro del input en la vista móvil (misma UX que en desktop) */
  .search-row .search-input-wrapper {
    position: relative;
    width: 100%;
    max-width: none; /* allow it to expand to the right */
  }

  .search-row .search-input-wrapper .filter-input {
    padding-right: 72px !important; /* espacio para iconos en móvil */
    width: 100%;
    max-width: none !important;
  }

  .search-row .search-input-wrapper .search-icon,
  .search-row .search-input-wrapper .clear-icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 3;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    font-size: 18px;
  }

  .search-row .search-input-wrapper .search-icon { right: 12px; }
  .search-row .search-input-wrapper .clear-icon { right: 44px; }

  /* En móvil la búsqueda ocupa todo el ancho disponible */
  .search-row #busquedaUnificada {
    width: 100% !important;
    max-width: none !important; /* let it fill the container to the right */
    margin: 0 !important;
    box-sizing: border-box;
  }

  /* Pequeño espacio entre el input de descripción y los botones */
  .search-row .filter-input {
    margin-bottom: 8px !important;
  }

  .search-row .filter-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-start;
    width: 100%;
  }
  /* Ajustes específicos: hacer que los selects 'estado' y 'borrado' ocupen todo el ancho en móvil
     para que sus opciones se vean completas (solo afecta a pantallas <= 576px). */
  .filters-row .filter-group {
    width: 100%;
  }
  #estado,
  #borrado {
    width: 100% !important;
    max-width: none !important;
    box-sizing: border-box;
  }
  /* Mostrar únicamente las tres primeras columnas (número, fecha, incidencia) en móvil
     ocultando el resto de columnas de la tabla para mejorar legibilidad. No aplicar
     esta regla a la fila `.mobile-extra-row` (contiene los 3 campos extra). */
  .user-table tr:not(.mobile-extra-row) th:not(.col-numero):not(.col-fecha):not(.col-incidencia),
  .user-table tr:not(.mobile-extra-row):not(.mobile-header-row) th:not(.col-numero):not(.col-fecha):not(.col-incidencia),
  .user-table tr:not(.mobile-extra-row):not(.mobile-header-row) td:not(.col-numero):not(.col-fecha):not(.col-incidencia) {
    display: none !important;
  }
  /* Fila extra que contiene tres campos adicionales, visible solo en móvil */
  .mobile-extra-row { display: table-row; }
  .mobile-extra {
    display: flex;
    gap: 12px;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .mobile-extra .item { flex: 1 1 30%; font-size: 13px; }
  /* Mostrar fila de encabezados extra en móvil */
  .mobile-header-row { display: table-row; }
  .mobile-header-row th { padding: 0; border-bottom: none; }
  .mobile-header-extra { display:flex; gap:12px; justify-content:space-between; align-items:center; width:100%; }
  .mobile-header-extra .mh-item {
    flex: 1 1 30%;
    font-weight:700;
    font-size:13px;
    color: #fff;
    /* permitir que los títulos ocupen hasta 2 líneas y se ajusten */
    white-space: normal !important;
    line-height: 1.05 !important;
  }

  /* Style the mobile second header to match the main header and make it sticky (JS will set top) */
  .user-table thead tr.mobile-header-row th {
    background-color: #99172A !important;
    color: #fff !important;
    /* aumentar el padding vertical para que la segunda fila tenga más altura y permita 2 líneas */
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }

/* NOTE: column alignment for desktop is handled in the larger media query
   @media (min-width: 769px) further below where all header TH and body TD
   padding-left is normalized to 12px and the actions column is centered. */

  /* Asegurar que ambas filas del thead queden sticky y no se solapen con las filas de datos */
  .user-table thead tr:first-child th {
    position: sticky !important;
    top: 0 !important;
    z-index: 10000 !important;
  }
  .user-table thead tr.mobile-header-row th {
    position: sticky !important;
    /* top será aplicado por JS al calcular la altura de la primera fila */
    z-index: 9999 !important;
  }

  /* Ocultar 'asignado' solo en las filas principales (no en la fila extra móvil) */
  .user-table tr:not(.mobile-extra-row) th.col-asignado,
  .user-table tr:not(.mobile-extra-row) td.col-asignado {
    display: none !important;
    visibility: hidden !important;
  }

  /* Asegurar que la fila extra y sus items estén siempre visibles en móvil */
  .mobile-extra-row { display: table-row !important; }
  .mobile-extra .item { display: block !important; visibility: visible !important; }

  /* Mostrar en móvil únicamente los títulos de la segunda fila correspondientes al contenido extra
     (asignado, ubicacion, categoria). Ocultar el resto de celdas de esa fila. */
  .user-table tr.second-title-row { display: table-row !important; }
  .user-table tr.second-title-row th { display: none !important; visibility: hidden !important; }
  .user-table tr.second-title-row th.col-asignado,
  .user-table tr.second-title-row th.col-ubicacion,
  .user-table tr.second-title-row th.col-categoria {
    display: table-cell !important;
    visibility: visible !important;
    font-weight: 700 !important;
    text-align: left !important;
    padding: 6px 8px !important;
    background-color: #f5f5f5 !important;
  }

  /* Asegurar que los encabezados de la PRIMERA fila para esas columnas estén ocultos en móvil
     para que no compitan visualmente con la segunda fila. */
  .user-table thead tr:first-child th.col-asignado,
  .user-table thead tr:first-child th.col-ubicacion,
  .user-table thead tr:first-child th.col-categoria {
    display: none !important;
    visibility: hidden !important;
  }
}

/* Fallback fuerte: forzar visibilidad de la segunda fila móvil si por alguna regla desaparece */
@media (max-width: 576px) {
  .user-table thead tr.second-row,
  .user-table thead tr.mobile-header-row {
    display: table-row !important;
    visibility: visible !important;
  }
  .user-table thead tr.second-row th,
  .user-table thead tr.mobile-header-row th {
    display: table-cell !important;
    visibility: visible !important;
    position: sticky !important;
    top: 48px !important; /* fallback top just below first header */
    z-index: 11000 !important;
    background-color: #99172A !important; /* rojo corporativo */
    color: #fff !important;
  }
}

.tv-backdrop {
  position: fixed !important;
  inset: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: rgba(0,0,0,0.5) !important;
  z-index: 2147483000 !important;
  padding: 12px;
  box-sizing: border-box;
  pointer-events: auto;
  overflow-y: auto;
}

.tv-viewport {
  position: relative !important;
  z-index: 2147483001 !important;
  width: auto;
  max-width: calc(100% - 32px);
  max-height: calc(100vh - 40px);
  overflow: auto;
  box-sizing: border-box;
  margin: 0;
}

.tv-viewport > * {
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* Styles for idle warning modal and paused banner */
.idle-paused-banner {
  position: fixed;
  right: 16px;
  bottom: 16px;
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
  padding: 10px 12px;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.08);
  z-index: 130000;
}
.idle-paused-inner { display:flex; gap:12px; align-items:center; }
.modal-fixed { max-width: 520px; }

/* Estilos para acciones móviles */
.mobile-actions-btn {
  min-width: 36px;
  min-height: 36px;
  padding: 0;
  line-height: 1;
  font-size: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.mobile-actions-menu {
  position: absolute;
  background: #fff;
  border: 1px solid #ddd;
  box-shadow: 0 6px 18px rgba(0,0,0,0.08);
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 1000;
  right: 8px;
}

.mobile-action-item .btn {
  width: 100%;
  text-align: left;
}

/* Botón de alternar filtros junto al botón Nuevo ticket (estilo similar) */
.filters-toggle-btn {
  display: none;
  margin-left: 8px;
  padding: 8px 14px;
  border-radius: 5px;
  background-color: #6c757d;
  color: #fff;
  border: none;
  font-weight: bold;
}

.filters-toggle-btn:hover {
  background-color: #5a6268;
}

/* Ocultar las acciones de escritorio en móvil y mostrar el botón '+' */
@media (max-width: 576px) {
  .desktop-actions { display: none; }
  .mobile-actions-btn { display: inline-flex; }
  .filters-toggle-btn { display: inline-flex; }
}

/* Mobile: asegurar que el botón de filtros muestre texto y flecha en blanco */
@media (max-width: 576px) {
  .filters-toggle-btn,
  .filters-toggle-btn .btn-text,
  .filters-toggle-btn .arrow {
    color: #fff !important;
  }
}

@media (min-width: 577px) {
  .mobile-actions-btn, .mobile-actions-menu { display: none; }
}

/* Kebab removed: restore default mobile '+' action button behavior */
@media (max-width: 576px) {
  /* No kebab-specific rules. Keep existing mobile '+' button visible. */
}

/* Mantener el título sticky en móvil y desplazar el cabezal de la tabla debajo de él */
@media (max-width: 576px) {
  /* No hacer sticky el título en móvil; solo sticky el encabezado de la tabla */
  .title-container {
    position: static;
  }

  .user-table thead th {
    position: sticky !important;
    top: 0 !important; /* encabezado pegado a la parte superior en móvil */
    z-index: 9999 !important;
    background-color: #99172A !important; /* corporate red */
    color: #fff !important;
    /* Aumentar padding vertical para dejar espacio a la segunda fila (dos filas visibles) */
    padding-top: 18px !important;
    padding-bottom: 18px !important;
  }

  /* Asegurar que la segunda fila de títulos quede fija justo debajo de la primera */
  .user-table thead tr.second-title-row th {
    position: sticky !important;
    /* top será calculado y aplicado por JS al calcular la altura de la primera fila */
    z-index: 9998 !important;
    background-color: #99172A !important;
    color: #fff !important;
    will-change: top;
  }

  /* Si hay elementos superpuestos, asegúrate de desplazar la segunda fila el alto del título y otros sticky bars */
  .title-container {
    z-index: 10000; /* estar por encima del thead si fuera necesario */
  }

  .table-container {
    /* allow flex growth and scrolling on very small devices */
    flex: 1 1 auto;
    min-height: 0;
  }
}

@media (max-width: 768px) {
  .tv-viewport { width: calc(100% - 24px); max-width: calc(100% - 24px); max-height: calc(100vh - 24px); border-radius: 6px; }
}

/* Estilos locales para centrar el modal teleported SIN overlay */
.no-backdrop-viewport {
  position: fixed !important;
  left: 50% !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 2147483647 !important;
  pointer-events: auto !important;
  max-width: calc(100% - 32px) !important;
  max-height: calc(100vh - 40px) !important;
  overflow: auto !important;
  box-sizing: border-box !important;
}

.no-backdrop-viewport > * {
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* Fallback fixed header for mobile second row */
.mobile-fixed-second-header {
  display: none;
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  z-index: 2147483640;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
  padding: 6px 12px;
  box-sizing: border-box;
  justify-content: space-between;
  gap: 12px;
  display: flex;
  align-items: flex-start;
}
.mobile-fixed-second-header .mfh-item {
  flex: 1 1 30%;
  font-weight: 700;
  font-size: 13px;
}

@media (min-width: 577px) {
  .mobile-fixed-second-header { display: none !important; }
}

/* Forzar que el header fijo de segunda fila esté visible y fijo justo bajo la primera fila en móvil */
@media (max-width: 576px) {
  .mobile-fixed-second-header {
    display: flex !important;
    position: fixed !important;
    left: 0 !important;
    right: 0 !important;
    top: 56px !important; /* fallback: justo debajo del primer header */
    z-index: 12000 !important;
    background: #99172A !important; /* match header color */
    color: #fff !important;
    padding: 8px 12px !important;
    box-sizing: border-box !important;
    justify-content: space-between !important;
    gap: 12px !important;
    align-items: center !important;
  }
  .mobile-fixed-second-header .mfh-item { color: #fff !important; font-weight:700 !important; }
}

/* Ensure the visual header area above the table is white */
.table-header {
  background-color: #ffffff;
}

/* Make the whole header stack sticky inside the scrollable table container
   so both title rows stay fixed above the tbody while it scrolls. */
.table-container > .headers-stack {
  position: sticky !important;
  top: 0 !important;
  z-index: 12002 !important;
  background: #ffffff !important; /* ensure it covers content below */
}

/* Prevent nested sticky rules from conflicting when headers-stack is sticky */
.table-container > .headers-stack .table-header-wrapper {
  position: static !important;
}
.table-container .user-table thead th {
  /* Let the headers be normal flow inside the sticky headers-stack; avoid double-sticky */
  position: static !important;
  top: auto !important;
}

/* Spacer inserted at the end of the content to guarantee visual gap before footer */
.content-spacer {
  flex: 0 0 auto; /* do not grow/shrink */
  height: 16px; /* reduced desktop gap */
  width: 100%;
}

@media (max-width: 576px) {
  .content-spacer {
    height: 12px; /* smaller gap on mobile */
  }
}

/* Overrides: Make the mobile "second header" blue and align the fixed fallback with container padding */
@media (max-width: 576px) {
  /* Table-based second header row (sticky THs inside the table) */
  .user-table thead tr.second-row th,
  .user-table thead tr.mobile-header-row th {
    background-color: #99172A !important; /* rojo corporativo */
    color: #fff !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }

  /* Fixed fallback: constrain left/right so it aligns with the page content padding (welcome-container uses 20px) */
  .mobile-fixed-second-header {
    left: 20px !important;
    right: 20px !important;
    width: auto !important;
    max-width: calc(100% - 40px) !important;
  background: #99172A !important;
  color: #fff !important;
  border-radius: 6px !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
  padding: 10px 12px !important;
  align-items: center !important;
  }

  /* Mostrar el elemento en flujo entre el thead y el body en móvil (no fixed) */
  .mobile-fixed-second-header {
    position: static !important;
    display: flex !important;
    left: auto !important;
    right: auto !important;
    top: auto !important;
    /* No gap between first header row and this element */
    margin: 0 !important;
    max-width: none !important;
    width: auto !important;
    /* Match padding so the two bars sit flush */
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    min-height: 0 !important;
    border-top: none !important;
    box-shadow: none !important;
  }

  /* High-specificity fallback: force corporate red if other CSS overrides remain */
  .mobile-fixed-second-header,
  .user-table thead tr.second-row th,
  .user-table thead tr.mobile-header-row th {
    background: #99172A !important;
    color: #fff !important;
  }

  /* Target the observed scoped attribute to further increase specificity */
  .mobile-fixed-second-header[data-v-0939a750],
  .user-table thead tr.second-row th[data-v-0939a750],
  .user-table thead tr.mobile-header-row th[data-v-0939a750] {
    background: #99172A !important;
    color: #fff !important;
  }
  /* Ajuste: altura de la PRIMERA fila de títulos (global y en móvil).
     Reducimos padding-bottom para que no cree separación con la segunda fila. */
  .user-table thead tr.first-row th,
  .user-table thead tr:first-child th {
    padding-top: 12px !important;
    padding-bottom: 8px !important;
    min-height: 40px !important; /* altura razonable pero sin crear gap */
    line-height: 1.15 !important;
    box-sizing: border-box !important;
  }
}

/* Última defensa: forzar esquinas rectas para la segunda fila de encabezado en móvil
   Esto anula cualquier border-radius aplicado más arriba (incluyendo reglas con !important) */
@media (max-width: 576px) {
  .mobile-fixed-second-header,
  .user-table thead tr.second-row th,
  .user-table thead tr.mobile-header-row th {
    border-radius: 0 !important;
    box-shadow: none !important; /* eliminar sombra que puede dar sensación de bordes redondeados */
    overflow: visible !important;
  }

  /* También targetear la versión observada/scoped si existe el atributo data-v para mayor especificidad */
  .mobile-fixed-second-header[data-v-0939a750],
  .user-table thead tr.second-row th[data-v-0939a750],
  .user-table thead tr.mobile-header-row th[data-v-0939a750] {
    border-radius: 0 !important;
    box-shadow: none !important;
  }
}

/* Desktop: alinear los títulos de la tabla a la izquierda para que coincidan con el contenido */
@media (min-width: 769px) {
  /* Force header cells to align left and use the same left padding as body cells
     so that titles line up exactly with their column content. */
  .user-table.user-table-header th {
    text-align: left !important;
    padding-left: 12px !important;
    vertical-align: middle !important;
  }

  /* Ensure body cells keep the same left padding for perfect alignment */
  .user-table.user-table-body td {
    text-align: left !important;
    padding-left: 12px !important;
  }

/* Default (mobile): allow tables to size naturally and enable horizontal scroll when needed */
.user-table {
  table-layout: auto;
  width: 100%;
  overflow: visible;
}

  /* Ensure tables use fixed layout on desktop for predictable column widths
     but allow auto layout on small screens for responsive wrapping. */
  .user-table,
  .user-table.user-table-header,
  .user-table.user-table-body {
    table-layout: fixed !important;
    width: 100% !important;
  }

  /* Align the actions column title and content to the left so the header matches the icons */
  .user-table.user-table-header th.col-acciones,
  .user-table.user-table-body td.col-acciones {
    text-align: left !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  /* Neutralize any extra left margin/padding from inner elements inside header cells */
  .user-table.user-table-header th .material-icons,
  .user-table.user-table-header th button {
    margin-left: 0 !important;
    padding-left: 0 !important;
  }
}

/* Badge para indicar ticket cerrado */
.status-badge {
  display: inline-block;
  margin-left: 8px;
  background: #6c757d; /* gris oscuro */
  color: #fff;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 12px;
  vertical-align: middle;
}

/* Icono de solo consulta un poco más neutro */
.material-icons.action-icon {
  font-size: 18px;
  vertical-align: middle;
  color: #27323a;
}

.material-icons.action-icon[title="Ver"],
.material-icons.action-icon[title='Ver'] {
  color: #4a5568; /* tono neutro para consulta */
}

/* More specific per-column alignment for desktop to guarantee exact left edges
   between header titles and their column content. Adjust paddings and remove
   inner offsets that can cause misalignment. */
@media (min-width: 769px) {
  /* Ensure consistent box-sizing so padding calculations match */
  .user-table.user-table-header th,
  .user-table.user-table-body td {
    box-sizing: border-box !important;
  }

  /* Per-column paddings: tune left/right so header text aligns with cell content. */
  .user-table.user-table-header th.col-numero,
  .user-table.user-table-body td.col-numero {
    padding-left: 12px !important;
    padding-right: 8px !important;
    width: 6.5rem; /* help keep widths stable */
  }

  .user-table.user-table-header th.col-fecha,
  .user-table.user-table-body td.col-fecha {
    padding-left: 12px !important;
    padding-right: 8px !important;
    width: 9rem;
  }

  .user-table.user-table-header th.col-incidencia,
  .user-table.user-table-body td.col-incidencia {
    padding-left: 12px !important;
    padding-right: 12px !important;
    /* Fix incidencia column width to 550px on desktop */
    width: 550px !important;
    min-width: 550px !important;
    max-width: 550px !important;
  }

  .user-table.user-table-header th.col-creado,
  .user-table.user-table-body td.col-creado {
    padding-left: 12px !important;
    padding-right: 8px !important;
    /* Fix creado column width to 260px on desktop */
    width: 260px !important;
    min-width: 260px !important;
    max-width: 260px !important;
  }

  .user-table.user-table-header th.col-ubicacion,
  .user-table.user-table-body td.col-ubicacion {
    padding-left: 12px !important;
    padding-right: 8px !important;
    /* Set ubicacion column width to 190px on desktop */
    width: 190px !important;
    min-width: 190px !important;
    max-width: 190px !important;
  }

  /* Ensure the <col> elements (colgroup) match the widths for alignment */
  .user-table.user-table-header col.c-ubicacion,
  .user-table.user-table-body col.c-ubicacion {
    width: 190px !important;
  }

  .user-table.user-table-header th.col-categoria,
  .user-table.user-table-body td.col-categoria {
    padding-left: 12px !important;
    padding-right: 8px !important;
    /* Set categoria column width to 190px on desktop */
    width: 190px !important;
    min-width: 190px !important;
    max-width: 190px !important;
  }

  .user-table.user-table-header col.c-categoria,
  .user-table.user-table-body col.c-categoria {
    width: 190px !important;
  }

  .user-table.user-table-header th.col-asignado,
  .user-table.user-table-body td.col-asignado {
    padding-left: 12px !important;
    padding-right: 8px !important;
    /* Fix asignado column width to 260px on desktop */
    width: 260px !important;
    min-width: 260px !important;
    max-width: 260px !important;
  }

  .user-table.user-table-header col.c-asignado,
  .user-table.user-table-body col.c-asignado {
    width: 260px !important;
  }

  .user-table.user-table-header th.col-nivel,
  .user-table.user-table-body td.col-nivel {
    padding-left: 12px !important;
    padding-right: 8px !important;
    /* Set prioridad (nivel) column width to 120px on desktop */
    width: 120px !important;
    min-width: 120px !important;
    max-width: 120px !important;
  }

  .user-table.user-table-header col.c-nivel,
  .user-table.user-table-body col.c-nivel {
    width: 120px !important;
  }

  .user-table.user-table-header th.col-estado,
  .user-table.user-table-body td.col-estado {
    padding-left: 12px !important;
    padding-right: 8px !important;
    /* Set estado column width to 140px on desktop */
    width: 140px !important;
    min-width: 140px !important;
    max-width: 140px !important;
  }

  .user-table.user-table-header col.c-estado,
  .user-table.user-table-body col.c-estado {
    width: 140px !important;
  }

  /* Centered actions column kept narrow */
  .user-table.user-table-header th.col-acciones,
  .user-table.user-table-body td.col-acciones {
    padding-left: 12px !important;
    padding-right: 12px !important;
    /* Increase actions column width on desktop so all icons fit in one row */
    width: 215px !important;
    min-width: 215px !important;
    max-width: 215px !important;
  }

  .user-table.user-table-header col.c-acciones,
  .user-table.user-table-body col.c-acciones {
    width: 215px !important;
  }

  /* Remove left margins from icons/buttons inside both headers and cells to avoid offsets */
  .user-table th > .material-icons,
  .user-table td > .material-icons,
  .user-table th button,
  .user-table td button {
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Ensure action icons inside the actions cell are left-aligned and spaced */
  .user-table.user-table-body td.col-acciones .action-icon,
  .user-table.user-table-header th.col-acciones .action-icon {
    margin-left: 0 !important;
    margin-right: 6px !important;
  }
}

/* Scale fixed pixel widths (designed for 1920px) proportionally for intermediate
   desktop sizes (1280px - 1919px) so the table fits without horizontal scroll.
   This keeps the visual proportions and left-alignment while avoiding overflow. */
@media (min-width: 1280px) and (max-width: 1919px) {
  /* incidencia 550px @ 1920 */
  .user-table.user-table-header th.col-incidencia,
  .user-table.user-table-body td.col-incidencia,
  .user-table.user-table-header col.c-incidencia,
  .user-table.user-table-body col.c-incidencia {
    width: calc(100vw * 550 / 1920) !important;
    min-width: calc(100vw * 550 / 1920) !important;
    max-width: calc(100vw * 550 / 1920) !important;
  }

  /* creado / asignado 260px @ 1920 */
  .user-table.user-table-header th.col-creado,
  .user-table.user-table-body td.col-creado,
  .user-table.user-table-header col.c-creado,
  .user-table.user-table-body col.c-creado,
  .user-table.user-table-header th.col-asignado,
  .user-table.user-table-body td.col-asignado,
  .user-table.user-table-header col.c-asignado,
  .user-table.user-table-body col.c-asignado {
    width: calc(100vw * 260 / 1920) !important;
    min-width: calc(100vw * 260 / 1920) !important;
    max-width: calc(100vw * 260 / 1920) !important;
  }

  /* ubicacion / categoria 190px @ 1920 */
  .user-table.user-table-header th.col-ubicacion,
  .user-table.user-table-body td.col-ubicacion,
  .user-table.user-table-header col.c-ubicacion,
  .user-table.user-table-body col.c-ubicacion,
  .user-table.user-table-header th.col-categoria,
  .user-table.user-table-body td.col-categoria,
  .user-table.user-table-header col.c-categoria,
  .user-table.user-table-body col.c-categoria {
    width: calc(100vw * 190 / 1920) !important;
    min-width: calc(100vw * 190 / 1920) !important;
    max-width: calc(100vw * 190 / 1920) !important;
  }

  /* prioridad (nivel) 120px @ 1920 */
  .user-table.user-table-header th.col-nivel,
  .user-table.user-table-body td.col-nivel,
  .user-table.user-table-header col.c-nivel,
  .user-table.user-table-body col.c-nivel {
    width: calc(100vw * 120 / 1920) !important;
    min-width: calc(100vw * 120 / 1920) !important;
    max-width: calc(100vw * 120 / 1920) !important;
  }

  /* estado 140px @ 1920 */
  .user-table.user-table-header th.col-estado,
  .user-table.user-table-body td.col-estado,
  .user-table.user-table-header col.c-estado,
  .user-table.user-table-body col.c-estado {
    width: calc(100vw * 140 / 1920) !important;
    min-width: calc(100vw * 140 / 1920) !important;
    max-width: calc(100vw * 140 / 1920) !important;
  }

  /* acciones 215px @ 1920 */
  .user-table.user-table-header th.col-acciones,
  .user-table.user-table-body td.col-acciones,
  .user-table.user-table-header col.c-acciones,
  .user-table.user-table-body col.c-acciones {
    width: calc(100vw * 215 / 1920) !important;
    min-width: calc(100vw * 215 / 1920) !important;
    max-width: calc(100vw * 215 / 1920) !important;
  }

  /* keep table-layout fixed on these screens as well for consistent column sizing */
  .user-table,
  .user-table.user-table-header,
  .user-table.user-table-body {
    table-layout: fixed !important;
  }
}

/* Ajuste fino: desplazar los TH hacia la derecha 3 caracteres en escritorio
   para que su margen izquierdo coincida visualmente con el contenido del TD
   sin tocar los anchos de las columnas. */
@media (min-width: 769px) {
  .table-header-wrapper .user-table thead th {
    /* base padding-left es 12px en TD; aquí lo movemos +3ch para TH */
    padding-left: calc(12px + 3ch) !important;
  }

  /* Si se desea aplicar sólo a columnas específicas, descomentar/ajustar: */
  /* .table-header-wrapper .user-table thead th.col-creado { padding-left: calc(12px + 3ch) !important; } */
}

</style>